import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { convertToGrayScale, loadImageDimensions, parseAmount } from "../modules/invoiceUtils";

export const Generate4InchInvoiceReceiptM2 = async (
  data,
  qrCodeDataUrl,
  terms,
  invoiceConfig,
  selectedTheme, 
  storeSettings
) => {    
     const {
    documentConfig,hasPayment       
  } = invoiceConfig;
 
  const mm = (pt) => pt / 2.83465;
  const taxRows1 = documentConfig?.items;
  const itemHeight = documentConfig?.items?.length * mm(65); // Approx 45pt per item row
  const taxHeight = Object.keys(taxRows1)?.length * mm(55);
  const footerHeight = mm(350); // Total for branding, thank you, and payment boxes
  const headerHeight = mm(350); // Total for logo, store info, and invoice details

  // Total dynamic height with a small buffer
  const dynamicHeight = headerHeight + itemHeight + taxHeight + footerHeight;

  const doc = new jsPDF({
    orientation: "p",
    unit: "mm",
    format: [101.6, dynamicHeight + mm(30)],
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  let y = mm(10);

  const isIGST =
    documentConfig?.fromData?.statecode &&
    documentConfig?.toData?.statecode &&
    documentConfig?.fromData?.statecode !== documentConfig?.toData?.statecode;

  const taxRows = documentConfig?.items?.reduce((acc, product) => {
    const taxPercentage = product?.taxpercentage || 0;
    const taxAmount = product?.lineTotalTaxAmount || 0;
    const basePrice = product?.lineTotalBasePrice || 0;
    if (!acc[taxPercentage]) {
      acc[taxPercentage] = { basePrice: 0, sgst: 0, cgst: 0, igst: 0 };
    }
    acc[taxPercentage].basePrice += basePrice;
    if (isIGST) {
      acc[taxPercentage].igst += taxAmount;
    } else {
      acc[taxPercentage].sgst += taxAmount / 2;
      acc[taxPercentage].cgst += taxAmount / 2;
    }
    return acc;
  }, {});
  const checkPageBreak = (doc, y, buffer = mm(20)) => {
    const pageHeight = doc.internal.pageSize.getHeight();
    if (y + buffer >= pageHeight) {
      doc.addPage();
      return mm(14);
    }
    return y;
  };
  // const storeBoxStartY = y;

   const imagePath =  documentConfig?.image;

  // const imagePath = settings?.saleProfileData?.image
  //   ?  settings?.saleProfileData?.image
  //   : "/assets/images/logo-light.png";

  if (documentConfig?.image) {
    try {
      // Step 1: Convert image URL to base64
      const response = await fetch(imagePath, { mode: "cors" });
      const blob = await response.blob();
      let base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

      // Step 2: Convert to grayscale
      base64 = await convertToGrayScale(base64);

      // Step 3: Get natural dimensions
      const { width: naturalWidth, height: naturalHeight } =
        await loadImageDimensions(base64);

      // Step 4: Resize to fit dynamically within maxWidth and maxHeight
      const maxWidth = mm(80); // maximum width in pt for 3-inch receipt
      const maxHeight = mm(40); // maximum height in pt
      const widthRatio = maxWidth / naturalWidth;
      const heightRatio = maxHeight / naturalHeight;
      const ratio = Math.min(widthRatio, heightRatio, 1); // don't upscale

      const imgWidth = naturalWidth * ratio;
      const imgHeight = naturalHeight * ratio;

      // Step 5: Center the logo
      const logoX = (pageWidth - imgWidth) / 2;

      doc.addImage(base64, "PNG", logoX, y, imgWidth, imgHeight);

      y += imgHeight + mm(20); // spacing after logo
    } catch (err) {
      console.warn("Logo failed to load:", err);
    }
  }

  let margin = mm(15);
  const drawDashedLine = (yPos) => {
    doc.setLineDash([mm(3), mm(3)], 0);
    doc.line(margin, yPos, pageWidth - margin, yPos);
    doc.setLineDash([]); // Reset to solid
  };
  // Header

  doc.setFont("NotoSansMono-Bold", "bold");

  doc.setFontSize(13);
  const storeTitle =  documentConfig?.fromData?.title || "Store";
  doc.splitTextToSize(storeTitle, pageWidth - mm(30)).forEach((line) => {
    doc.text(line, pageWidth / 2, y, { align: "center" });
    y += mm(13);
  });
  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(9);
  const seller = documentConfig?.fromData;
  const sellerAddressParts = [
    seller?.addressline1,
    [seller?.state].filter(Boolean).join(", "),
  ].filter(Boolean);
  doc
    .splitTextToSize(sellerAddressParts.join(", "), pageWidth - mm(50))
    .forEach((line) => {
      doc.text(line, pageWidth / 2, y, { align: "center" });
      y += mm(9);
    });

  // if (seller?.email) {
  //   doc.text(`Email: ${seller.email}`, pageWidth / 2, y, { align: "center" });
  //   y += 16;
  // }
  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(9); // Slightly smaller to match UI

  const addressText = `${seller?.district} ${seller?.country} - ${seller?.pincode}`;
  doc.text(addressText, pageWidth / 2, y, { align: "center" });
  y += mm(9);
  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(9);
  const gstText = `GST: ${seller?.gstnumber || ""}`;
  const phoneText = `Ph: ${documentConfig?.fromData?.phone
            ? `${documentConfig.fromData.phone}`
            : ""
        }${
          documentConfig?.fromData?.additionalPhone
            ? `, ${documentConfig.fromData.additionalPhone}`
            : ""
        }`;
  const fssainumber = `FSSAI : ${seller?.fssai || ""}`;
  const pannumber = `PAN : ${seller?.pannumber || ""}`;
  const cinnumber = `CIN :${seller?.cinnumber || ""}`;

  const horizontalPadding = mm(12);
  const leftStart = horizontalPadding;
  const rightEnd = pageWidth - horizontalPadding;
  // Draw GST: Aligned to the far left margin
  doc.text(gstText, leftStart, y, { align: "left" });

  // Draw Phone: Aligned to the far right margin
  doc.text(phoneText, rightEnd, y, { align: "right" });
  y += mm(9);
  {
    seller?.fssai && doc.text(fssainumber, rightEnd, y, { align: "right" });
  }

  // Draw Phone: Aligned to the far right margin
  doc.text(pannumber, leftStart, y, { align: "left" });
  y += mm(9);
  if (seller?.cinnumber) {
    doc.text(cinnumber, leftStart, y, { align: "left" });
    y += mm(9);
  }
  // drawDashedLine(y);
  const storeBoxPadding = 4;

  doc.setLineWidth(mm(1));
  doc.setLineDash([mm(1), mm(1)]); // solid border

  y += mm(7);
  const invoiceTitle =
    data?.status === "CANCELLED"
      ? "CANCELLED TAX INVOICE"
      :documentConfig?.title;

  doc.setFont("NotoSansMono-Bold", "bold").setFontSize(11);
  const textWidth = doc.getTextWidth(invoiceTitle);
  const gap = mm(8); // Space between the line and the text
  margin = mm(10);
  // 5. Draw the Lines
  let centerX = pageWidth / 2;
  doc.text(invoiceTitle, centerX, y, { align: "center" });
  doc.setLineWidth(mm(1));
  doc.setLineDash([mm(3), mm(3)]); // Use [0] for a solid line, [2, 2] for dashed like your image

  // Left Line: starts at margin, ends just before the text
  doc.line(margin, y - mm(4), centerX - textWidth / 2 - gap, y - mm(4));

  // Right Line: starts just after the text, ends at margin
  doc.line(
    centerX + textWidth / 2 + gap,
    y - mm(4),
    pageWidth - margin,
    y - mm(4),
  );

  // 6. Reset Dash and move Y
  doc.setLineDash([]);
  y += mm(15);

  const { date, time } = documentConfig?.Date;
  const lineHeight = mm(12); // Adjusted for tighter spacing seen in UI
  const leftColX = mm(8); // Left margin
  const rightColX = pageWidth / 2 + mm(10); // Start of the second column
  const drawField = (label, value, xPos, yPos) => {
    doc.setFont("NotoSansMono-Regular", "normal").setFontSize(10);
    const fieldText = `${label} : ${value}`;
    doc.text(fieldText, xPos, yPos);
  };
  drawField(
   documentConfig?.numberLabel ,
    `${documentConfig?.number}`,
    leftColX,
    y,
  );
  // drawField("Counter", "02", rightColX, y); // Replace "02" with your dynamic counter data
  y += lineHeight;

  // Row 2: Date
  drawField("Date", `${date}, ${time}`, leftColX, y);
  y += lineHeight;

  // Row 3: Cashier
  // drawField("Cashier", "Stejo", leftColX, y); // Replace "Stejo" with your dynamic cashier data

  const labelX = 8; // Left-aligned label start
  const colonX = 110; // Colon aligned at this point
  const valueX = 120; // Value starts after colon

  // y += mm(12);

  const buyer = documentConfig?.toData;
  const hasBuyerInfo =
    buyer &&
    (buyer?.name ||
      buyer?.phone ||
      buyer?.addressline1 ||
      buyer?.email ||
      buyer?.gstnumber ||
      buyer?.pannumber ||
      buyer?.cinnumber);

  // --- BUYER INFORMATION SECTION ---
  if (hasBuyerInfo) {
    // 1. Section Header: Clean and Centered
    doc.setFont("NotoSansMono-Regular", "normal").setFontSize(10);
    doc.text("BUYER INFORMATION", pageWidth / 2, y, { align: "center" });
    y += mm(13);

    const leftMargin = mm(10);
    const lineHeight = mm(9);

    //   // 2. Buyer Name: Bold and Uppercase for Impact
    if (buyer?.name) {
      doc.setFont("NotoSansMono-Bold", "bold").setFontSize(9);
      doc.text(buyer?.name.toUpperCase(), leftMargin, y);
      y += lineHeight;
    }

    //   // 3. Buyer Details: Smaller and Regular font
    doc.setFont("NotoSansMono-Regular", "normal").setFontSize(9);

    // Contact Row (Phone & Email)
    if (buyer?.phone || buyer?.email) {
      const contactLine = [
        buyer?.phone ? `Ph: ${buyer?.phone}` : null,
        buyer?.email ? `Email: ${buyer?.email}` : null,
      ]
        .filter(Boolean)
        .join(" | ");

      doc.text(contactLine, leftMargin, y);
      y += lineHeight;
    }

    // Address: Handling long text with splitTextToSize
    const addressParts = [
      buyer?.addressline1,
      [buyer?.state, buyer?.district, buyer?.country]
        .filter(Boolean)
        .join(", "),
      buyer?.pincode ? `- ${buyer?.pincode}` : null,
    ]
      .filter(Boolean)
      .join(", ");

    if (addressParts) {
      const splitAddress = doc.splitTextToSize(
        addressParts,
        pageWidth - mm(20),
      );
      splitAddress.forEach((line) => {
        doc.text(line, leftMargin, y);
        y += lineHeight;
      });
    }

    // 4. Statutory IDs (GST, PAN, CIN): Compact layout
    const idList = [
      { label: "GST: ", value: buyer.gstnumber, bold: true,},      
      { label: "PAN: ", value: buyer.pannumber ,bold: true,},
      { label: "CIN: ", value: buyer.cinnumber,bold: true, },
      { label: "FSSAI: ", value: buyer.fssai,bold: true, },
      { label: "Reference No: ", value: buyer.referenceid, bold: false, },
    ];
    idList.forEach((item) => {
      if (item.value) {
        const leftMargin = mm(10);
if (item.bold) {
      doc.setFont("NotoSansMono-Bold", "bold");
      } else {
        doc.setFont("NotoSansMono-Regular", "normal");
      }  
        // 1. Draw the Label in BOLD
        doc.setFontSize(9);
        doc.text(item?.label, leftMargin, y);

        // 2. Calculate label width so the value starts exactly after it
        const labelWidth = doc.getTextWidth(item?.label);

        // 3. Draw the Value in REGULAR
        doc.setFont("NotoSansMono-Regular", "normal");
        doc.text(`${item?.value}`, leftMargin + labelWidth, y);

        // 4. Move to next line
        y += mm(9);
      }
    });

    y += mm(5); // Extra spacing before the items table starts
  }

  const tableMargin = mm(8);
  const tableWidth = pageWidth - tableMargin * 2;

  autoTable(doc, {
    startY: y,
    margin: { left: tableMargin, right: tableMargin },
    theme: "plain",
    // 1. Set global border styles
    styles: {
      fontSize: 10,
      cellPadding: { top: 0.5, right: 1, bottom: 0.2, left: 1 },
      lineColor: "black", // Light gray border
      lineWidth: 1,
      font: "NotoSansMono-Regular",
    },
    head: [
      // Header Row 1: "Items" label span
      [
        {
          content: "Items",
          colSpan: 3,
          styles: {
            halign: "left",
            cellPadding: 1,
            fontStyle: "bold",
            lineWidth: { left: mm(1), right: mm(1), top: mm(1), bottom: 0 },
          },
        },
      ],
      // Header Row 2: Rate, Qty, Total
      [
        {
          content: "Rate",
          cellPadding: 1,
          styles: {
            halign: "center",
            fontStyle: "bold",
            lineWidth: { left: mm(1), top: 0, bottom: mm(1) },
          },
        },
        {
          content: "Qty",
          cellPadding: 1,
          styles: {
            halign: "center",
            fontStyle: "bold",
            lineWidth: { top: 0, bottom: mm(1) },
          },
        },
        {
          content: "Total",
          cellPadding: 1,
          styles: {
            halign: "center",
            fontStyle: "bold",
            lineWidth: { right: mm(1), top: 0, bottom: mm(1) },
          },
        },
      ],
    ],
    body: documentConfig?.items.flatMap((item) => [
      // Line 1: Item Name (Full width)
      [
        {
          content: item?.title + (item.note ? ` (${item.note})` : ""),
          colSpan: 3,
          styles: {
            halign: "left",
            lineWidth: { left: mm(1), right: mm(1), top: 0, bottom: 0 },
          },
        },
      ],
      // Line 2: Values (Rate, Qty, Total)
      [
        {
          content: `${item?.soldprice}`,
          styles: {
            halign: "center",
            lineWidth: { left: mm(1), top: 0, bottom: 0 },
          },
        },
        {
          content: `${item?.quantity} ${(() => {
            const unitCode = item?.isSecondaryUnit
              ? item?.unitConfig?.secondaryUnitCode
              : item?.unitConfig?.primaryUnitCode;
            return unitCode || "";
          })()}`,
          styles: { halign: "center", lineWidth: { top: 0, bottom: 0 } },
        },
        {
          content: `${item?.lineTotalSoldPrice}`,
          styles: {
            halign: "center",
            lineWidth: { right: mm(1), top: 0, bottom: 0 },
          },
        },
      ],
    ]),
    // 3. Close the bottom border on the last row
    didDrawCell: (data) => {
      if (data.row.index === data.table.body.length - 1) {
        doc.setDrawColor(0);
        doc.setLineWidth(mm(1));
        doc.line(
          data.cell.x,
          data.cell.y + data.cell.height,
          data.cell.x + data.cell.width,
          data.cell.y + data.cell.height,
        );
      }
    },
  });

  y = doc.lastAutoTable.finalY + mm(10);

  // Set dimensions for the boxes
  const boxPadding = 8;
  let boxWidth = (pageWidth - mm(24)) / 2; // Split page width minus margins
  const boxHeight = mm(45); // Height to accommodate two lines of text
  const startX = mm(8);
  const rightBoxX = startX + boxWidth + mm(8);

  // --- 1. Draw Left Box (Qty & Items) ---
  doc.setDrawColor(0); // Changed from 200 to 150 for a darker, printable gray
  doc.setLineWidth(mm(1)); // Light gray border
  doc.roundedRect(startX, y, boxWidth, boxHeight, mm(3), mm(3), "S");

  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(12);
  // Qty Line
  // doc.text("Qty :", startX + mm(5), y + mm(18));
  // doc.text(`${data.totalqty || 0}`, startX + boxWidth - mm(5), y + mm(18), { align: "right" });
  // Items Line
  doc.text("Items :", startX + mm(5), y + mm(28));
  doc.text(`${data?.totalItems}`, startX + boxWidth - mm(5), y + mm(28), {
    align: "right",
  });

  // --- 2. Draw Right Box (Total Amount) ---
  doc.roundedRect(rightBoxX, y, boxWidth, boxHeight, mm(3), mm(3), "S");

  doc.setFont("NotoSansMono-Bold", "bold").setFontSize(12);
  const totalLabel = "Grand Total";
  const totalValue = `${documentConfig?.grandTotal}`; // Assuming raw number as per UI
  const boxCenterX = rightBoxX + boxWidth / 2;

  // Draw Label: Centered horizontally
  doc.text(totalLabel, boxCenterX, y + mm(18), { align: "center" });

  doc.setFontSize(13);
  doc.text(totalValue, boxCenterX, y + mm(38), { align: "center" });

  // // Center the content within the right box
  // const combinedText = `${totalLabel} ${totalValue}`;
  // const textWidth1 = doc.getTextWidth(combinedText);
  // const textStartX = rightBoxX + (boxWidth - textWidth1) / 2;

  // doc.text(totalLabel, textStartX, y + 26);
  // doc.text(totalValue, textStartX + doc.getTextWidth(totalLabel) + 4, y + 26);

  y += boxHeight + mm(10); // Advance Y position for next section

  // y = checkPageBreak(doc, y);
  const boxMargin = mm(8);

  // 1. Prepare payment list (Only show modes with value)
  const payments = [
    { label: "Cash", amount: documentConfig?.cashPaid },
    { label: "Card", amount: documentConfig?.cardPaid },
    {
      label: "Online Pay",
      amount: documentConfig?.onlinePaid,
    },
    { label: "UPI", amount: documentConfig?.upiPaid },
    { label: "Balance Due", amount: documentConfig?.balanceDue },
  ].filter((p) => Math.abs(parseAmount(p.amount)) > 0);

  autoTable(doc, {
    startY: y,
    theme: "plain",
    margin: { left: boxMargin, right: boxMargin },
    styles: {
      font: "NotoSansMono-Regular",
      fontSize: 9,
      cellPadding: { top: 0.5, right: 1, bottom: 0.2, left: 1 },
      lineColor: "black",
      lineWidth: 1,
    },
    head:hasPayment
    ? [
      [
        {
          content: "PAYMENT MODE",
          colSpan: 3,
          styles: {
            font: "NotoSansMono-Bold",
            fontSize: 9,
            cellPadding: 1,
            fontStyle: "bold",
            lineWidth: { top: mm(1), left: mm(1), right: mm(1), bottom: mm(1) },
          },
        },
      ],
    ]:[],
    body: [
      ...(hasPayment
      ?payments?.map((p) => [
        {
          content: p?.label,
          styles: { fontSize: 9, lineWidth: { left: mm(1) } },
        },
        { content: ":", styles: { halign: "center", lineWidth: 0 } },
        {
          content: `${(p?.amount)}`,
          styles: { halign: "right", lineWidth: { right: mm(1) }, fontSize: 9 },
        },
      ]):[]),
      // Bottom Right Footer for Tender and Balance
      [
        {
          // content: `Tender: ${CurrencyConverter.toCurrency(data.cashamount || 0||0, "INR")}\nBalance: ${CurrencyConverter.toCurrency(data.cashchange || 0||0, "INR")}`,
          content: `Grand Total:   ${documentConfig?.grandTotal}`,
          colSpan: 5,
          styles: {
            halign: "right",
            font: "NotoSansMono-Bold",
            fontStyle: "bold",
            fontSize: 12,
            cellPadding: 2,
            lineWidth: { left: mm(1), right: mm(1), bottom: mm(1), top: mm(1) },
          },
        },
      ],
    ],
    columnStyles: {
      0: { cellWidth: "auto" },
      1: { cellWidth: "20" },
      2: { cellWidth: "auto" },
    },
  });

  y = doc.lastAutoTable.finalY + mm(15);

  const itemBoxPadding = 4;
  // y = checkPageBreak(doc, y)

  doc.setDrawColor(0);
  doc.setLineWidth(mm(0.5));
  const taxHead = isIGST
    ? [["Tax", "Amount", "IGST"]]
    : [["Tax", "Amount", "SGST", "CGST"]];

  const taxBody = Object.entries(taxRows)
    .filter(([percent]) => parseFloat(percent) > 0)
    .map(([percent, t]) =>
      isIGST
        ? [`${percent}%`, `${t?.basePrice}`, `${t?.igst}`]
        : [`${percent}%`, `${t?.basePrice}`, `${t?.sgst}`, `${t?.cgst}`],
    );
  // 1. Define margins and capture the starting Y position
  const taxBoxMargin = mm(8);
  const taxNotoSansMononalPadding = mm(5);
  const taxBoxWidth = pageWidth - taxBoxMargin * 2;
  const taxStartPointY = y; // Save where the box starts

  if (taxBody?.length > 0) {
    // 2. Generate the table FIRST
    autoTable(doc, {
      startY: taxStartPointY + taxNotoSansMononalPadding,
      head: [
        taxHead[0].map((cell, colIndex) => ({
          content: cell,
          styles: {
            font: "NotoSansMono-Bold",
            fontSize: 9,
            fontStyle: "bold",
            cellPadding: { top: 0.5, right: 1, bottom: 0.2, left: 1 },
            halign: colIndex === 0 ? "left" : "right",
          },
        })),
      ],
      body: taxBody?.map((row) =>
        row.map((cell, colIndex) => ({
          content: cell,
          styles: {
            font: "NotoSansMono-Regular",
            fontStyle: "normal",
            fontSize: 9,
            cellPadding: { top: 0.5, right: 1, bottom: 0.2, left: 1 },
            halign: colIndex === 0 ? "left" : "right",
          },
        })),
      ),
      theme: "plain",
      styles: {
        font: "NotoSansMono-Regular",
        fontStyle: "normal",
        fontSize: 9,
        // cellPadding: 2,
        lineWidth: 0,
      },
      margin: {
        left: taxBoxMargin + taxNotoSansMononalPadding,
        right: taxBoxMargin + taxNotoSansMononalPadding,
      },
    });

    // 3. CALCULATE HEIGHT: Use the final Y of the table
    const taxEndPointY = doc.lastAutoTable.finalY + taxNotoSansMononalPadding;
    const dynamicBoxHeight = taxEndPointY - taxStartPointY;

    // 4. DRAW THE BOX: Now we know the exact height needed
    doc.setDrawColor(0);
    doc.setLineWidth(mm(0.5));
    doc.rect(taxBoxMargin, taxStartPointY, taxBoxWidth, dynamicBoxHeight, "S");

    // 5. Update global Y for the next section
    y = taxEndPointY + mm(10);
  }

  // y = checkPageBreak(doc, y);
  if (parseAmount(documentConfig?.totalDiscount) > 0) {
    const boxMargin = mm(8);
    const boxHeight = mm(20);
    const boxWidth = pageWidth - boxMargin * 2;
    // o 1
    // 1. Draw the Container Box
    doc.setDrawColor(0); // Light Gray border
    doc.setLineWidth(mm(1));
    doc.rect(boxMargin, y, boxWidth, boxHeight, "S");

    // 2. Set Font for the content
    doc.setFont("NotoSansMono-Regular", "normal").setFontSize(10);

    const savedAmount = `${documentConfig?.totalDiscount}`;
    doc.text(`YOU SAVED :  ${savedAmount}`, boxMargin + mm(4), y + mm(14));

    // doc.text(savedAmount, pageWidth - (boxMargin + 4), y + 14, { align: "right" });

    y += boxHeight + mm(10);
  }
  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(14);

  y += mm(5);
  doc.setFontSize(10);
  doc.text("Thank you for your purchase!", pageWidth / 2, y, {
    align: "center",
  });
  y += mm(20);
  doc.setFont("NotoSansMono-Bold", "bold");
  doc.text("** Visit us Again **", pageWidth / 2, y, { align: "center" });
  const thankBoxPadding=10;
  y += mm(10);

  doc.setFont("NotoSansMono-Regular", "normal");
  doc.text("Powered By", pageWidth / 2, y, { align: "center" });
  y += mm(10);
  doc.setFontSize(10);
  const sitePrefix = "www.";
  const siteBold = "NegoBill";
  const siteSuffix = ".com";
  const prefixWidth = doc.getTextWidth(sitePrefix);
  const boldWidth = doc.getTextWidth(siteBold);
  const suffixWidth = doc.getTextWidth(siteSuffix);
  const totalWidth = prefixWidth + boldWidth + suffixWidth;
  const startX1 = (pageWidth - totalWidth) / 2;

  if (storeSettings?.negobillBranding) {
    doc.setFont("NotoSansMono-Regular", "normal");
    doc.text(sitePrefix, startX1, y);
    doc.setFont("NotoSansMono-Bold", "bold");
    doc.text(siteBold, startX1 + prefixWidth, y);
    doc.setFont("NotoSansMono-Regular", "normal");
    doc.text(siteSuffix, startX1 + prefixWidth + boldWidth, y);
    y += mm(3);
  }
  // setPrint(false);
  return doc;
};

export const Generate4InchBillReceiptM2 = async (
   data,
  qrCodeDataUrl,
  terms,
  invoiceConfig,
  selectedTheme,  
  storeSettings
) => {
     const {
    documentConfig,  hasPayment     
  } = invoiceConfig;
 
  const mm = (pt) => pt / 2.83465;
  const taxRows1 = documentConfig?.items;
  const itemHeight = documentConfig?.items?.length * mm(65); // Approx 45pt per item row
  const taxHeight = Object.keys(taxRows1)?.length * mm(55);
  const footerHeight = mm(350); // Total for branding, thank you, and payment boxes
  const headerHeight = mm(350); // Total for logo, store info, and invoice details

  // Total dynamic height with a small buffer
  const dynamicHeight = headerHeight + itemHeight + taxHeight + footerHeight;

  const doc = new jsPDF({
    orientation: "p",
    unit: "mm",
    format: [101.6, dynamicHeight + mm(30)],
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  let y = mm(10);

  const isIGST =
    documentConfig?.fromData?.statecode &&
    documentConfig?.toData?.statecode &&
    documentConfig?.fromData.statecode !== documentConfig?.toData.statecode;

  const taxRows = documentConfig?.items?.reduce((acc, product) => {
    const taxPercentage = product?.taxpercentage || 0;
    const taxAmount = product?.lineTotalTaxAmount || 0;
    const basePrice = product?.lineTotalBasePrice || 0;
    if (!acc[taxPercentage]) {
      acc[taxPercentage] = { basePrice: 0, sgst: 0, cgst: 0, igst: 0 };
    }
    acc[taxPercentage].basePrice += basePrice;
    if (isIGST) {
      acc[taxPercentage].igst += taxAmount;
    } else {
      acc[taxPercentage].sgst += taxAmount / 2;
      acc[taxPercentage].cgst += taxAmount / 2;
    }
    return acc;
  }, {});
  const checkPageBreak = (doc, y, buffer = mm(20)) => {
    const pageHeight = doc.internal.pageSize.getHeight();
    if (y + buffer >= pageHeight) {
      doc.addPage();
      return mm(14);
    }
    return y;
  };
  // const storeBoxStartY = y;

     const imagePath =  documentConfig?.image;


  // const imagePath = settings?.saleProfileData?.image
  //   ?  settings?.saleProfileData?.image
  //   : "/assets/images/logo-light.png";

  if (documentConfig?.image) {
    try {
      // Step 1: Convert image URL to base64
      const response = await fetch(imagePath, { mode: "cors" });
      const blob = await response.blob();
      let base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

      // Step 2: Convert to grayscale
      base64 = await convertToGrayScale(base64);

      // Step 3: Get natural dimensions
      const { width: naturalWidth, height: naturalHeight } =
        await loadImageDimensions(base64);

      // Step 4: Resize to fit dynamically within maxWidth and maxHeight
      const maxWidth = mm(80); // maximum width in pt for 3-inch receipt
      const maxHeight = mm(40); // maximum height in pt
      const widthRatio = maxWidth / naturalWidth;
      const heightRatio = maxHeight / naturalHeight;
      const ratio = Math.min(widthRatio, heightRatio, 1); // don't upscale

      const imgWidth = naturalWidth * ratio;
      const imgHeight = naturalHeight * ratio;

      // Step 5: Center the logo
      const logoX = (pageWidth - imgWidth) / 2;

      doc.addImage(base64, "PNG", logoX, y, imgWidth, imgHeight);

      y += imgHeight + mm(20); // spacing after logo
    } catch (err) {
      console.warn("Logo failed to load:", err);
    }
  }

  let margin = mm(15);
  const drawDashedLine = (yPos) => {
    doc.setLineDash([mm(3), mm(3)], 0);
    doc.line(margin, yPos, pageWidth - margin, yPos);
    doc.setLineDash([]); // Reset to solid
  };
  // Header

  doc.setFont("NotoSansMono-Bold", "bold");

  doc.setFontSize(13);
  const storeTitle =  documentConfig?.fromData?.title || "Store";
  doc.splitTextToSize(storeTitle, pageWidth - mm(30)).forEach((line) => {
    doc.text(line, pageWidth / 2, y, { align: "center" });
    y += mm(13);
  });
  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(9);
  const seller = documentConfig?.fromData;
  const sellerAddressParts = [
    seller?.addressline1,
    [seller?.state].filter(Boolean).join(", "),
  ].filter(Boolean);
  doc
    .splitTextToSize(sellerAddressParts.join(", "), pageWidth - mm(50))
    .forEach((line) => {
      doc.text(line, pageWidth / 2, y, { align: "center" });
      y += mm(9);
    });

  // if (seller?.email) {
  //   doc.text(`Email: ${seller.email}`, pageWidth / 2, y, { align: "center" });
  //   y += 16;
  // }
  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(9); // Slightly smaller to match UI

  const addressText = `${seller?.district} ${seller?.country} - ${seller?.pincode}`;
  doc.text(addressText, pageWidth / 2, y, { align: "center" });
  y += mm(9);
  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(9);
  // const gstText = `GST: ${seller?.gstnumber || ""}`;
  const phoneText = `Ph: ${documentConfig?.fromData?.phone
            ? `${documentConfig.fromData.phone}`
            : ""
        }${
          documentConfig?.fromData?.additionalPhone
            ? `, ${documentConfig.fromData.additionalPhone}`
            : ""
        }`;
  const fssainumber = `FSSAI : ${seller?.fssai || ""}`;
  const pannumber = `PAN : ${seller?.pannumber || ""}`;
  const cinnumber = `CIN :${seller?.cinnumber || ""}`;

  const horizontalPadding = mm(12);
  const leftStart = horizontalPadding;
  const rightEnd = pageWidth - horizontalPadding;
  // Draw GST: Aligned to the far left margin

  doc.text(pannumber, leftStart, y, { align: "left" });
  // Draw Phone: Aligned to the far right margin
  doc.text(phoneText, rightEnd, y, { align: "right" });
  y += mm(9);
  {
    seller?.fssai && doc.text(fssainumber, leftStart, y, { align: "left" });
    y += mm(9);
  }

  // Draw Phone: Aligned to the far right margin

  {
    seller?.cinnumber && doc.text(cinnumber, leftStart, y, { align: "left" });
  }
  y += mm(9);
  // drawDashedLine(y);
  const storeBoxPadding = 4;

  doc.setLineWidth(mm(1));
  doc.setLineDash([mm(1), mm(1)]); // solid border

  y += mm(7);
 const invoiceTitle =
    data?.status === "CANCELLED"
      ? "CANCELLED TAX INVOICE"
      :documentConfig?.title;

  doc.setFont("NotoSansMono-Bold", "bold").setFontSize(11);
  const textWidth = doc.getTextWidth(invoiceTitle);
  const gap = mm(8); // Space between the line and the text
  margin = mm(10);
  // 5. Draw the Lines
  let centerX = pageWidth / 2;
  doc.text(invoiceTitle, centerX, y, { align: "center" });
  doc.setLineWidth(mm(1));
  doc.setLineDash([mm(3), mm(3)]); // Use [0] for a solid line, [2, 2] for dashed like your image

  // Left Line: starts at margin, ends just before the text
  doc.line(margin, y - mm(4), centerX - textWidth / 2 - gap, y - mm(4));

  // Right Line: starts just after the text, ends at margin
  doc.line(
    centerX + textWidth / 2 + gap,
    y - mm(4),
    pageWidth - margin,
    y - mm(4),
  );

  // 6. Reset Dash and move Y
  doc.setLineDash([]);
  y += mm(15);

  const { date, time } = documentConfig?.Date;
  const lineHeight = mm(12); // Adjusted for tighter spacing seen in UI
  const leftColX = mm(8); // Left margin
  const rightColX = pageWidth / 2 + mm(10); // Start of the second column
  const drawField = (label, value, xPos, yPos) => {
    doc.setFont("NotoSansMono-Regular", "normal").setFontSize(10);
    const fieldText = `${label} : ${value}`;
    doc.text(fieldText, xPos, yPos);
  };
  drawField( documentConfig?.numberlabel, `${ documentConfig?.number}`, leftColX, y);
  // drawField("Counter", "02", rightColX, y); // Replace "02" with your dynamic counter data
  y += lineHeight;

  // Row 2: Date
  drawField("Date", `${date}, ${time}`, leftColX, y);
  y += lineHeight;

  // Row 3: Cashier
  // drawField("Cashier", "Stejo", leftColX, y); // Replace "Stejo" with your dynamic cashier data

  const labelX = 8; // Left-aligned label start
  const colonX = 110; // Colon aligned at this point
  const valueX = 120; // Value starts after colon

  // y += mm(12);

  const buyer = documentConfig?.toData;
  const hasBuyerInfo =
    buyer &&
    (buyer?.name ||
      buyer?.phone ||
      buyer?.addressline1 ||
      buyer?.email ||
      buyer?.gstnumber ||
      buyer?.pannumber ||
      buyer?.cinnumber);

  // --- BUYER INFORMATION SECTION ---
  if (hasBuyerInfo) {
    // 1. Section Header: Clean and Centered
    doc.setFont("NotoSansMono-Regular", "normal").setFontSize(10);
    doc.text("BUYER INFORMATION", pageWidth / 2, y, { align: "center" });
    y += mm(13);

    const leftMargin = mm(10);
    const lineHeight = mm(9);

    //   // 2. Buyer Name: Bold and Uppercase for Impact
    if (buyer?.name) {
      doc.setFont("NotoSansMono-Bold", "bold").setFontSize(9);
      doc.text(buyer?.name.toUpperCase(), leftMargin, y);
      y += lineHeight;
    }

    //   // 3. Buyer Details: Smaller and Regular font
    doc.setFont("NotoSansMono-Regular", "normal").setFontSize(9);

    // Contact Row (Phone & Email)
    if (buyer?.phone || buyer?.email) {
      const contactLine = [
        buyer?.phone ? `Ph: ${buyer?.phone}` : null,
        buyer?.email ? `Email: ${buyer?.email}` : null,
      ]
        .filter(Boolean)
        .join(" | ");

      doc.text(contactLine, leftMargin, y);
      y += lineHeight;
    }

    // Address: Handling long text with splitTextToSize
    const addressParts = [
      buyer?.addressline1,
      [buyer?.state, buyer?.district, buyer?.country]
        .filter(Boolean)
        .join(", "),
      buyer?.pincode ? `- ${buyer?.pincode}` : null,
    ]
      .filter(Boolean)
      .join(", ");

    if (addressParts) {
      const splitAddress = doc.splitTextToSize(
        addressParts,
        pageWidth - mm(20),
      );
      splitAddress.forEach((line) => {
        doc.text(line, leftMargin, y);
        y += lineHeight;
      });
    }

    // 4. Statutory IDs (GST, PAN, CIN): Compact layout
     const idList = [
      { label: "GST: ", value: buyer.gstnumber, bold: true,},      
      { label: "PAN: ", value: buyer.pannumber ,bold: true,},
      { label: "CIN: ", value: buyer.cinnumber,bold: true, },
      { label: "FSSAI: ", value: buyer.fssai,bold: true, },
      { label: "Reference No: ", value: buyer.referenceid, bold: false, },
    ];
    idList.forEach((item) => {
      if (item.value) {
        const leftMargin = mm(10);
if (item.bold) {
      doc.setFont("NotoSansMono-Bold", "bold");
      } else {
        doc.setFont("NotoSansMono-Regular", "normal");
      }  
        // 1. Draw the Label in BOLD
       doc.setFontSize(9);
        doc.text(item.label, leftMargin, y);

        // 2. Calculate label width so the value starts exactly after it
        const labelWidth = doc.getTextWidth(item.label);

        // 3. Draw the Value in REGULAR
        doc.setFont("NotoSansMono-Regular", "normal");
        doc.text(`${item?.value}`, leftMargin + labelWidth, y);

        // 4. Move to next line
        y += mm(9);
      }
    });

    y += mm(5); // Extra spacing before the items table starts
  }

  const tableMargin = mm(8);
  const tableWidth = pageWidth - tableMargin * 2;

  autoTable(doc, {
    startY: y,
    margin: { left: tableMargin, right: tableMargin },
    theme: "plain",
    // 1. Set global border styles
    styles: {
      fontSize: 10,
      cellPadding: { top: 0.5, right: 1, bottom: 0.2, left: 1 },
      lineColor: "black", // Light gray border
      lineWidth: 1,
      font: "NotoSansMono-Regular",
    },
    head: [
      // Header Row 1: "Items" label span
      [
        {
          content: "Items",
          colSpan: 3,
          styles: {
            halign: "left",
            cellPadding: 1,
            fontStyle: "bold",
            lineWidth: { left: mm(1), right: mm(1), top: mm(1), bottom: 0 },
          },
        },
      ],
      // Header Row 2: Rate, Qty, Total
      [
        {
          content: "Rate",
          cellPadding: 1,
          styles: {
            halign: "center",
            fontStyle: "bold",
            lineWidth: { left: mm(1), top: 0, bottom: mm(1) },
          },
        },
        {
          content: "Qty",
          cellPadding: 1,
          styles: {
            halign: "center",
            fontStyle: "bold",
            lineWidth: { top: 0, bottom: mm(1) },
          },
        },
        {
          content: "Total",
          cellPadding: 1,
          styles: {
            halign: "center",
            fontStyle: "bold",
            lineWidth: { right: mm(1), top: 0, bottom: mm(1) },
          },
        },
      ],
    ],
    body: documentConfig?.items.flatMap((item) => [
      // Line 1: Item Name (Full width)
      [
        {
          content: item?.title + (item?.note ? ` (${item?.note})` : ""),
          colSpan: 3,
          styles: {
            halign: "left",
            lineWidth: { left: mm(1), right: mm(1), top: 0, bottom: 0 },
          },
        },
      ],
      // Line 2: Values (Rate, Qty, Total)
      [
        {
          content: `${item?.soldprice}`,
          styles: {
            halign: "center",
            lineWidth: { left: mm(1), top: 0, bottom: 0 },
          },
        },
        {
          content: `${item?.quantity} ${(() => {
            const unitCode = item?.isSecondaryUnit
              ? item?.unitConfig?.secondaryUnitCode
              : item?.unitConfig?.primaryUnitCode;
            return unitCode || "";
          })()}`,
          styles: { halign: "center", lineWidth: { top: 0, bottom: 0 } },
        },
        {
          content: `${item?.lineTotalSoldPrice}`,
          styles: {
            halign: "center",
            lineWidth: { right: mm(1), top: 0, bottom: 0 },
          },
        },
      ],
    ]),
    // 3. Close the bottom border on the last row
    didDrawCell: (data) => {
      if (data.row.index === data.table.body.length - 1) {
        doc.setDrawColor(0);
        doc.setLineWidth(mm(1));
        doc.line(
          data.cell.x,
          data.cell.y + data.cell.height,
          data.cell.x + data.cell.width,
          data.cell.y + data.cell.height,
        );
      }
    },
  });

  y = doc.lastAutoTable.finalY + mm(10);

  // Set dimensions for the boxes
  const boxPadding = 8;
  let boxWidth = (pageWidth - mm(24)) / 2; // Split page width minus margins
  const boxHeight = mm(45); // Height to accommodate two lines of text
  const startX = mm(8);
  const rightBoxX = startX + boxWidth + mm(8);

  // --- 1. Draw Left Box (Qty & Items) ---
  doc.setDrawColor(0); // Changed from 200 to 150 for a darker, printable gray
  doc.setLineWidth(mm(1)); // Light gray border
  doc.roundedRect(startX, y, boxWidth, boxHeight, mm(3), mm(3), "S");

  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(12);
  // Qty Line
  // doc.text("Qty :", startX + mm(5), y + mm(18));
  // doc.text(`${data.totalqty || 0}`, startX + boxWidth - mm(5), y + mm(18), { align: "right" });
  // Items Line
  doc.text("Items :", startX + mm(5), y + mm(28));
  doc.text(`${data?.totalItems}`, startX + boxWidth - mm(5), y + mm(28), {
    align: "right",
  });

  // --- 2. Draw Right Box (Total Amount) ---
  doc.roundedRect(rightBoxX, y, boxWidth, boxHeight, mm(3), mm(3), "S");

  doc.setFont("NotoSansMono-Bold", "bold").setFontSize(12);
  const totalLabel = "Grand Total";
  const totalValue = `${documentConfig?.grandTotal}`; // Assuming raw number as per UI
  const boxCenterX = rightBoxX + boxWidth / 2;

  // Draw Label: Centered horizontally
  doc.text(totalLabel, boxCenterX, y + mm(18), { align: "center" });

  doc.setFontSize(13);
  doc.text(totalValue, boxCenterX, y + mm(38), { align: "center" });

  // // Center the content within the right box
  // const combinedText = `${totalLabel} ${totalValue}`;
  // const textWidth1 = doc.getTextWidth(combinedText);
  // const textStartX = rightBoxX + (boxWidth - textWidth1) / 2;

  // doc.text(totalLabel, textStartX, y + 26);
  // doc.text(totalValue, textStartX + doc.getTextWidth(totalLabel) + 4, y + 26);

  y += boxHeight + mm(10); // Advance Y position for next section

  // y = checkPageBreak(doc, y);
  const boxMargin = mm(8);

  // 1. Prepare payment list (Only show modes with value)
  const payments = [
    { label: "Cash", amount: documentConfig?.cashPaid },
    { label: "Card", amount: documentConfig?.cardPaid },
    {
      label: "Online Pay",
      amount: documentConfig?.onlinePaid,
    },
    { label: "UPI", amount: documentConfig?.upiPaid },
    { label: "Balance Due", amount: documentConfig?.balanceDue },
  ].filter((p) => Math.abs(parseAmount(p.amount)) > 0);

  autoTable(doc, {
    startY: y,
    theme: "plain",
    margin: { left: boxMargin, right: boxMargin },
    styles: {
      font: "NotoSansMono-Regular",
      fontSize: 9,
      cellPadding: { top: 0.5, right: 1, bottom: 0.2, left: 1 },
      lineColor: "black",
      lineWidth: 1,
    },
    head: hasPayment
    ?[
      [
        {
          content: "PAYMENT MODE",
          colSpan: 3,
          styles: {
            font: "NotoSansMono-Bold",
            fontSize: 9,
            cellPadding: 1,
            fontStyle: "bold",
            lineWidth: { top: mm(1), left: mm(1), right: mm(1), bottom: mm(1) },
          },
        },
      ],
    ]:[],
    body: [
       ...(hasPayment
      ?payments?.map((p) => [
        {
          content: p?.label,
          styles: { fontSize: 9, lineWidth: { left: mm(1) } },
        },
        { content: ":", styles: { halign: "center", lineWidth: 0 } },
        {
          content: `${(p?.amount)}`,
          styles: { halign: "right", lineWidth: { right: mm(1) }, fontSize: 9 },
        },
      ]):[]),
      // Bottom Right Footer for Tender and Balance
      [
        {
          // content: `Tender: ${CurrencyConverter.toCurrency(data.cashamount || 0||0, "INR")}\nBalance: ${CurrencyConverter.toCurrency(data.cashchange || 0||0, "INR")}`,
          content: `Grand Total:   ${documentConfig?.grandTotal}`,
          colSpan: 5,
          styles: {
            halign: "right",
            font: "NotoSansMono-Bold",
            fontStyle: "bold",
            fontSize: 12,
            cellPadding: 2,
            lineWidth: { left: mm(1), right: mm(1), bottom: mm(1), top: mm(1) },
          },
        },
      ],
    ],
    columnStyles: {
      0: { cellWidth: "auto" },
      1: { cellWidth: "20" },
      2: { cellWidth: "auto" },
    },
  });

  y = doc.lastAutoTable.finalY + mm(15);

  const itemBoxPadding = 4;
  // y = checkPageBreak(doc, y)

  //  doc.setDrawColor(0);
  // doc.setLineWidth(mm(0.5));
  //     const taxHead = isIGST
  //       ? [["Tax", "Amount", "IGST"]]
  //       : [["Tax", "Amount", "SGST", "CGST"]];

  //     const taxBody = Object.entries(taxRows)
  //       .filter(([percent]) => parseFloat(percent) > 0)
  //       .map(([percent, t]) =>
  //         isIGST
  //           ? [
  //               `${percent}%`,
  //               `${CurrencyConverter.toCurrency(t.basePrice || 0, "INR")}`,
  //               `${CurrencyConverter.toCurrency(t.igst || 0, "INR")}`,
  //             ]
  //           : [
  //               `${percent}%`,
  //               `${CurrencyConverter.toCurrency(t.basePrice || 0, "INR")}`,
  //               `${CurrencyConverter.toCurrency(t.sgst || 0, "INR")}`,
  //               `${CurrencyConverter.toCurrency(t.cgst || 0, "INR")}`,
  //             ]
  //       );
  //       // 1. Define margins and capture the starting Y position
  // const taxBoxMargin = mm(8);
  // const taxNotoSansMononalPadding = mm(5);
  // const taxBoxWidth = pageWidth - (taxBoxMargin * 2);
  // const taxStartPointY = y; // Save where the box starts

  // if (taxBody.length > 0) {
  //     // 2. Generate the table FIRST
  //     autoTable(doc, {
  //         startY: taxStartPointY + taxNotoSansMononalPadding,
  //         head: [
  //             taxHead[0].map((cell, colIndex) => ({
  //                 content: cell,
  //                 styles: {
  //                     font: "NotoSansMono-Bold",
  //                     fontSize: 9,
  //                     fontStyle: "bold",
  //                     cellPadding: { top: 0.5, right: 1, bottom: 0.2, left: 1 },
  //                     halign: colIndex === 0 ? "left" : "right",
  //                 },
  //             })),
  //         ],
  //         body: taxBody.map((row) =>
  //             row.map((cell, colIndex) => ({
  //                 content: cell,
  //                 styles: {
  //                     font: "NotoSansMono-Regular",
  //                     fontStyle: "normal",
  //                     fontSize: 9,
  //                     cellPadding: { top: 0.5, right: 1, bottom: 0.2, left: 1 },
  //                     halign: colIndex === 0 ? "left" : "right",
  //                 },
  //             }))
  //         ),
  //         theme: "plain",
  //            styles: {
  //           font: "NotoSansMono-Regular",
  //           fontStyle: "normal",
  //           fontSize: 9,
  //           // cellPadding: 2,
  //           lineWidth: 0,
  //         },
  //         margin: {
  //             left: taxBoxMargin + taxNotoSansMononalPadding,
  //             right: taxBoxMargin + taxNotoSansMononalPadding
  //         },
  //     });

  //     // 3. CALCULATE HEIGHT: Use the final Y of the table
  //     const taxEndPointY = doc.lastAutoTable.finalY + taxNotoSansMononalPadding;
  //     const dynamicBoxHeight = taxEndPointY - taxStartPointY;

  //     // 4. DRAW THE BOX: Now we know the exact height needed
  //     doc.setDrawColor(0);
  //     doc.setLineWidth(mm(0.5));
  //     doc.rect(taxBoxMargin, taxStartPointY, taxBoxWidth, dynamicBoxHeight, 'S');

  //     // 5. Update global Y for the next section
  //     y = taxEndPointY + mm(10);
  // }

  //   const itemBoxPadding = 4;
  //     y = checkPageBreak(doc, y)
  //     const boxMargin = 8;

  //  const taxHead = isIGST
  //     ? [["Tax", "Amount", "IGST"]]
  //     : [["Tax", "Amount", "SGST", "CGST"]];

  //   const taxBody = Object.entries(taxRows)
  //     .filter(([percent]) => parseFloat(percent) > 0)
  //     .map(([percent, t]) =>
  //       isIGST
  //         ? [
  //             `${percent}%`,
  //             `${CurrencyConverter.toCurrency(t.basePrice || 0, "INR")}`,
  //             `${CurrencyConverter.toCurrency(t.igst || 0, "INR")}`,
  //           ]
  //         : [
  //             `${percent}%`,
  //             `${CurrencyConverter.toCurrency(t.basePrice || 0, "INR")}`,
  //             `${CurrencyConverter.toCurrency(t.sgst || 0, "INR")}`,
  //             `${CurrencyConverter.toCurrency(t.cgst || 0, "INR")}`,
  //           ]
  //     );
  //    y = checkPageBreak(doc, y)
  //         let tableRendered = false;
  //   if (taxBody.length > 0) {
  //     tableRendered = true;

  //     autoTable(doc, {
  //       startY: y,
  //       head: taxHead.map((row) =>
  //         row.map((cell, colIndex) => ({
  //           content: cell,
  //           styles: {
  //           font: "NotoSansMono-Bold",
  //                   fontSize: 14,
  //                   fontStyle: "bold",
  //               cellPadding: 2,
  //             halign: colIndex === 0 ? "left" : "right",
  //           },
  //         })),
  //       ),
  //       body: taxBody.map((row) =>
  //         row.map((cell, colIndex) => ({
  //           content: cell,
  //           styles: {
  //             font: "NotoSansMono-Regular",
  //             fontStyle: "normal",
  //              fontSize: 13,
  //             halign: colIndex === 0 ? "left" : "right",
  //           },
  //         })),
  //       ),
  //       theme: "plain",
  //       styles: {
  //         font: "NotoSansMono-Regular",
  //         fontStyle: "normal",
  //           fontSize: 12,
  //         cellPadding: 5,
  //         lineWidth: 0,
  //       },
  //       margin: { left: 10, right: 10 },
  //       columnStyles: {
  //         0: { halign: "left" },
  //         1: { halign: "right" },
  //         2: { halign: "right" },
  //         3: { halign: "right" },
  //       },
  //       didDrawCell: (data) => {
  //         const pageWidth = doc.internal.pageSize.getWidth();

  //         if (
  //           data.row.section === "head" &&
  //           data.row.index === 0 &&
  //           data.column.index === 0
  //         ) {
  //           const top = data.cell.y - 2;
  //           doc.setLineDash([1, 1], 0);
  //           doc.setDrawColor(150);
  //           doc.line(0, top, pageWidth - 5, top);
  //         }

  //         const isLastTaxRow =
  //           data.row.section === "body" &&
  //           data.row.index === taxBody.length - 1;

  //         if (isLastTaxRow && data.column.index === 0) {
  //           const bottom = data.cell.y + data.cell.height + 2;
  //           doc.setLineDash([1, 1], 0);
  //           doc.setDrawColor(150);
  //           doc.line(0, bottom, pageWidth - 5, bottom);
  //           doc.setLineDash([]);
  //         }
  //       },
  //     });
  //   }
  // if (tableRendered && doc.lastAutoTable?.finalY) {
  //     y = doc.lastAutoTable.finalY + 10;
  //   }
  // y = checkPageBreak(doc, y);
  if (parseAmount(documentConfig?.totalDiscount) > 0) {
    const boxMargin = mm(8);
    const boxHeight = mm(20);
    const boxWidth = pageWidth - boxMargin * 2;
    // o 1
    // 1. Draw the Container Box
    doc.setDrawColor(0); // Light Gray border
    doc.setLineWidth(mm(1));
    doc.rect(boxMargin, y, boxWidth, boxHeight, "S");

    // 2. Set Font for the content
    doc.setFont("NotoSansMono-Regular", "normal").setFontSize(10);

    const savedAmount = `${documentConfig?.totalDiscount}`;
    doc.text(`YOU SAVED :  ${savedAmount}`, boxMargin + mm(4), y + mm(14));

    // doc.text(savedAmount, pageWidth - (boxMargin + 4), y + 14, { align: "right" });

    y += boxHeight + mm(10);
  }
  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(14);

  y += mm(5);
  doc.setFontSize(10);
  doc.text("Thank you for your purchase!", pageWidth / 2, y, {
    align: "center",
  });
  // y += mm(20);
  // doc.setFont("NotoSansMono-Bold", "bold");
  // doc.text("** Visit us Again **", pageWidth / 2, y, { align: "center" });
  // const thankBoxPadding=10;
  y += mm(10);

  doc.setFont("NotoSansMono-Regular", "normal");
  doc.text("Powered By", pageWidth / 2, y, { align: "center" });
  y += mm(10);
  doc.setFontSize(10);
  const sitePrefix = "www.";
  const siteBold = "NegoBill";
  const siteSuffix = ".com";
  const prefixWidth = doc.getTextWidth(sitePrefix);
  const boldWidth = doc.getTextWidth(siteBold);
  const suffixWidth = doc.getTextWidth(siteSuffix);
  const totalWidth = prefixWidth + boldWidth + suffixWidth;
  const startX1 = (pageWidth - totalWidth) / 2;

  if (storeSettings?.negobillBranding) {
    doc.setFont("NotoSansMono-Regular", "normal");
    doc.text(sitePrefix, startX1, y);
    doc.setFont("NotoSansMono-Bold", "bold");
    doc.text(siteBold, startX1 + prefixWidth, y);
    doc.setFont("NotoSansMono-Regular", "normal");
    doc.text(siteSuffix, startX1 + prefixWidth + boldWidth, y);
    y += mm(3);
  }
  // setPrint(false);
  return doc;
};

export const Generate3InchInvoiceReceiptM2 = async (
    data,
  qrCodeDataUrl,
  terms,
  invoiceConfig,
  selectedTheme,  
  storeSettings,
) => {
   const {
    documentConfig, hasPayment      
  } = invoiceConfig;
  // Conversion constant: 1mm = 2.83465pt
  const mm = (pt) => pt / 2.83465;
  const taxRows1 = documentConfig?.items;
  const itemHeight = documentConfig?.items?.length * mm(45); // Approx 45pt per item row
  const taxHeight = Object.keys(taxRows1).length * mm(25); //25
  const footerHeight = mm(250); // Total for branding, thank you, and payment boxes
  const headerHeight = mm(250); // Total for logo, store info, and invoice details

  // Total dynamic height with a small buffer
  const dynamicHeight = headerHeight + itemHeight + taxHeight + footerHeight;

  const doc = new jsPDF({
    orientation: "p",
    unit: "mm",
    format: [74, dynamicHeight + mm(30)],
  });

  const pageWidth = doc.internal.pageSize.getWidth();

  let y = mm(10);

  const isIGST =
    documentConfig?.fromData?.statecode &&
    documentConfig?.toData?.statecode &&
    documentConfig?.fromData.statecode !== documentConfig?.toData?.statecode;

  const taxRows = documentConfig?.items?.reduce((acc, product) => {
    const taxPercentage = product?.taxpercentage || 0;
    const taxAmount = product?.lineTotalTaxAmount || 0;
    const basePrice = product?.lineTotalBasePrice || 0;
    if (!acc[taxPercentage]) {
      acc[taxPercentage] = { basePrice: 0, sgst: 0, cgst: 0, igst: 0 };
    }
    acc[taxPercentage].basePrice += basePrice;
    if (isIGST) {
      acc[taxPercentage].igst += taxAmount;
    } else {
      acc[taxPercentage].sgst += taxAmount / 2;
      acc[taxPercentage].cgst += taxAmount / 2;
    }
    return acc;
  }, {});
  const checkPageBreak = (doc, y, buffer = mm(20)) => {
    const pageHeight = doc.internal.pageSize.getHeight();
    if (y + buffer >= pageHeight) {
      doc.addPage();
      return mm(14);
    }
    return y;
  };
  // const storeBoxStartY = y;

    const imagePath =  documentConfig?.image;

  // const imagePath = settings?.saleProfileData?.image
  //   ?  settings?.saleProfileData?.image
  //   : "/assets/images/logo-light.png";

  if (documentConfig?.image) {
    try {
      // Step 1: Convert image URL to base64
      const response = await fetch(imagePath, { mode: "cors" });
      const blob = await response.blob();
      let base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

      // Step 2: Convert to grayscale
      base64 = await convertToGrayScale(base64);

      // Step 3: Get natural dimensions
      const { width: naturalWidth, height: naturalHeight } =
        await loadImageDimensions(base64);

      // Step 4: Resize to fit dynamically within maxWidth and maxHeight
      const maxWidth = mm(65); // maximum width in pt for 3-inch receipt
      const maxHeight = mm(40); // maximum height in pt
      const widthRatio = maxWidth / naturalWidth;
      const heightRatio = maxHeight / naturalHeight;
      const ratio = Math.min(widthRatio, heightRatio, 1); // don't upscale

      const imgWidth = naturalWidth * ratio;
      const imgHeight = naturalHeight * ratio;

      // Step 5: Center the logo
      const logoX = (pageWidth - imgWidth) / 2;

      doc.addImage(base64, "PNG", logoX, y, imgWidth, imgHeight);

      y += imgHeight + mm(10); // spacing after logo
    } catch (err) {
      console.warn("Logo failed to load:", err);
    }
  }

  let margin = mm(15);
  const drawDashedLine = (yPos) => {
    doc.setLineDash([mm(3), mm(3)], 0);
    doc.line(margin, yPos, pageWidth - margin, yPos);
    doc.setLineDash([]); // Reset to solid
  };
  // Header

  doc.setFont("NotoSansMono-Bold", "bold");

  doc.setFontSize(11);
  const storeTitle =   documentConfig?.fromData?.title || "Store";
  doc.splitTextToSize(storeTitle, pageWidth - mm(30)).forEach((line) => {
    doc.text(line, pageWidth / 2, y, { align: "center" });
    y += mm(13);
  });
  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(8);
  const seller = documentConfig?.fromData;
  const sellerAddressParts = [
    seller?.addressline1,
    [seller?.state].filter(Boolean).join(", "),
  ].filter(Boolean);
  doc
    .splitTextToSize(sellerAddressParts.join(", "), pageWidth - mm(50))
    .forEach((line) => {
      doc.text(line, pageWidth / 2, y, { align: "center" });
      y += mm(8);
    });

  // if (seller?.email) {
  //   doc.text(`Email: ${seller.email}`, pageWidth / 2, y, { align: "center" });
  //   y += 16;
  // }
  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(8); // Slightly smaller to match UI

  const addressText = `${seller?.district} ${seller?.country} - ${seller?.pincode}`;
  doc.text(addressText, pageWidth / 2, y, { align: "center" });
  y += mm(9);
  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(8);
  const gstText = seller?.gstnumber
  ? `GST: ${seller.gstnumber}`
  : "";
 const phoneNumbers = [
  documentConfig?.fromData?.phone,
  documentConfig?.fromData?.additionalPhone,
].filter(Boolean);

const phoneText = phoneNumbers.length
  ? `Ph: ${phoneNumbers.join(", ")}`
  : "";

  const fssainumber = `FSSAI : ${seller?.fssai || ""}`;
  const pannumber = `PAN : ${seller?.pannumber || ""}`;
  const cinnumber = `CIN :${seller?.cinnumber || ""}`;

  const horizontalPadding = mm(12);
  const leftStart = horizontalPadding;
  const rightEnd = pageWidth - horizontalPadding;
  if (phoneText) {
  const phoneMaxWidth = pageWidth - mm(20);

  const phoneLines = doc.splitTextToSize(
    phoneText,
    phoneMaxWidth,
  );

  doc.text(phoneLines, pageWidth / 2, y, {
    align: "center",
  });

  // Move according to actual phone lines
  y += phoneLines.length * mm(4) +2;
}
  // Draw GST: Aligned to the far left margin
  if (gstText) {
  doc.text(gstText, leftStart, y, {
    align: "left",
  });
}
  // Draw Phone: Aligned to the far right margin
  // doc.text(phoneText, rightEnd, y, { align: "right" });
  y += mm(9); 
// ---------- FSSAI + PAN ----------
if (seller?.fssai) {
  doc.text(fssainumber, rightEnd, y, {
    align: "right",
  });
}
if (seller?.pannumber) {
  doc.text(pannumber, leftStart, y, {
    align: "left",
  });
}
y += mm(9);
// ---------- CIN ----------
if (seller?.cinnumber) {
  doc.text(cinnumber, leftStart, y, {
    align: "left",
  });

  y += mm(12);
}

y+=2
  // drawDashedLine(y);
  const storeBoxPadding = 4;

  doc.setLineWidth(mm(1));
  doc.setLineDash([mm(1), mm(1)]); // solid border

  // y += mm(8);
 const invoiceTitle =
    data?.status === "CANCELLED"
      ? "CANCELLED TAX INVOICE"
      :documentConfig?.title;


  doc.setFont("NotoSansMono-Bold", "bold").setFontSize(11);
  const textWidth = doc.getTextWidth(invoiceTitle);
  const gap = mm(8); // Space between the line and the text
  margin = mm(10);
  // 5. Draw the Lines
  let centerX = pageWidth / 2;
  doc.text(invoiceTitle, centerX, y, { align: "center" });
  doc.setLineWidth(mm(1));
  doc.setLineDash([mm(3), mm(3)]); // Use [0] for a solid line, [2, 2] for dashed like your image

  // Left Line: starts at margin, ends just before the text
  doc.line(margin, y - mm(4), centerX - textWidth / 2 - gap, y - mm(4));

  // Right Line: starts just after the text, ends at margin
  doc.line(
    centerX + textWidth / 2 + gap,
    y - mm(4),
    pageWidth - margin,
    y - mm(4),
  );
  // 6. Reset Dash and move Y
  doc.setLineDash([]);
  y += mm(10);

  const { date, time } = documentConfig?.Date;
  const lineHeight = mm(9); // Adjusted for tighter spacing seen in UI
  const leftColX = mm(8); // Left margin
  const rightColX = pageWidth / 2 + mm(10); // Start of the second column
  const drawField = (label, value, xPos, yPos) => {
    doc.setFont("NotoSansMono-Regular", "normal").setFontSize(9);
    const fieldText = `${label} : ${value}`;
    doc.text(fieldText, xPos, yPos);
  };
  drawField(
    documentConfig?.numberLabel ,
    `${documentConfig?.number}`,   
    leftColX,
    y,
  );
  // drawField("Counter", "02", rightColX, y); // Replace "02" with your dynamic counter data
  y += lineHeight;

  // Row 2: Date
  drawField("Date", `${date}, ${time}`, leftColX, y);
  y += lineHeight;

  // Row 3: Cashier
  // drawField("Cashier", "Stejo", leftColX, y); // Replace "Stejo" with your dynamic cashier data

  const labelX = 8; // Left-aligned label start
  const colonX = 110; // Colon aligned at this point
  const valueX = 120; // Value starts after colon

  y += mm(6);

  const buyer = documentConfig?.toData;
  const hasBuyerInfo =
    buyer &&
    (buyer?.name ||
      buyer?.phone ||
      buyer?.addressline1 ||
      buyer?.email ||
      buyer?.gstnumber ||
      buyer?.pannumber ||
      buyer?.cinnumber);

  // --- BUYER INFORMATION SECTION ---
  if (hasBuyerInfo) {
    // 1. Section Header: Clean and Centered
    doc.setFont("NotoSansMono-Regular", "normal").setFontSize(8);
    doc.text("BUYER INFORMATION", pageWidth / 2, y, { align: "center" });
    y += mm(10);

    const leftMargin = mm(8);
    const lineHeight = mm(9);

    //   // 2. Buyer Name: Bold and Uppercase for Impact
    if (buyer?.name) {
      doc.setFont("NotoSansMono-Bold", "bold").setFontSize(8);
      doc.text(buyer?.name.toUpperCase(), leftMargin, y);
      y += lineHeight;
    }

    // 3. Buyer Details: Smaller and Regular font
    doc.setFont("NotoSansMono-Regular", "normal").setFontSize(8);

    // Contact Row (Phone & Email)
    if (buyer?.phone || buyer?.email) {
      const contactLine = [
        buyer?.phone ? `Ph: ${buyer?.phone}` : null,
        buyer?.email ? `Email: ${buyer?.email}` : null,
      ]
        .filter(Boolean)
        .join(" | ");

      doc.text(contactLine, leftMargin, y);
      y += lineHeight;
    }

    // Address: Handling long text with splitTextToSize
    const addressParts = [
      buyer?.addressline1,
      [buyer?.state, buyer?.district, buyer?.country]
        .filter(Boolean)
        .join(", "),
      buyer?.pincode ? `- ${buyer?.pincode}` : null,
    ]
      .filter(Boolean)
      .join(", ");

    if (addressParts) {
      const splitAddress = doc.splitTextToSize(
        addressParts,
        pageWidth - mm(20),
      );
      splitAddress.forEach((line) => {
        doc.text(line, leftMargin, y);
        y += lineHeight;
      });
    }

    // 4. Statutory IDs (GST, PAN, CIN): Compact layout
     const idList = [
      { label: "GST: ", value: buyer.gstnumber, bold: true,},      
      { label: "PAN: ", value: buyer.pannumber ,bold: true,},
      { label: "CIN: ", value: buyer.cinnumber,bold: true, },
      { label: "FSSAI: ", value: buyer.fssai,bold: true, },
      { label: "Reference No: ", value: buyer.referenceid, bold: false, },
    ];
    idList.forEach((item) => {
      if (item.value) {
        const leftMargin = mm(8);
if (item.bold) {
      doc.setFont("NotoSansMono-Bold", "bold");
      } else {
        doc.setFont("NotoSansMono-Regular", "normal");
      }  
        // 1. Draw the Label in BOLD
        doc.setFontSize(8);
        doc.text(item?.label, leftMargin, y);

        // 2. Calculate label width so the value starts exactly after it
        const labelWidth = doc.getTextWidth(item?.label);

        // 3. Draw the Value in REGULAR
        doc.setFont("NotoSansMono-Regular", "normal");
        doc.text(`${item?.value}`, leftMargin + labelWidth, y);

        // 4. Move to next line
        y += mm(9);
      }
    });

    y += mm(3); // Extra spacing before the items table starts
  }

  // Define table boundaries
  const tableMargin = mm(8);
  const tableWidth = pageWidth - tableMargin * 2;

  autoTable(doc, {
    startY: y,
    margin: { left: tableMargin, right: tableMargin },
    theme: "plain",
    // 1. Set global border styles
    styles: {
      fontSize: 8,
      cellPadding: { top: 0.5, right: 1, bottom: 0.2, left: 1 },
      lineColor: "black", // Light gray border
      lineWidth: 1,
      font: "NotoSansMono-Regular",
    },
    head: [
      // Header Row 1: "Items" label span
      [
        {
          content: "Items",
          cellPadding: 1,
          colSpan: 3,
          styles: {
            halign: "left",
            fontStyle: "bold",
            lineWidth: { left: mm(1), right: mm(1), top: mm(1), bottom: 0 },
          },
        },
      ],
      // Header Row 2: Rate, Qty, Total
      [
        {
          content: "Rate",
          cellPadding: 1,
          styles: {
            halign: "center",
            fontStyle: "bold",
            lineWidth: { left: mm(1), top: 0, bottom: mm(1) },
          },
        },
        {
          content: "Qty",
          cellPadding: 1,
          styles: {
            halign: "center",
            fontStyle: "bold",
            lineWidth: { top: 0, bottom: mm(1) },
          },
        },
        {
          content: "Total",
          cellPadding: 1,
          styles: {
            halign: "center",
            fontStyle: "bold",
            lineWidth: { right: mm(1), top: 0, bottom: mm(1) },
          },
        },
      ],
    ],
    body: documentConfig?.items.flatMap((item) => [
      // Line 1: Item Name (Full width)
      [
        {
          content: item?.title + (item?.note ? ` (${item?.note})` : ""),
          colSpan: 3,
          styles: {
            halign: "left",
            lineWidth: { left: mm(1), right: mm(1), top: 0, bottom: 0 },
          },
        },
      ],
      // Line 2: Values (Rate, Qty, Total)
      [
        {
          content: `${item?.soldprice}`,
          styles: {
            halign: "center",
            lineWidth: { left: mm(1), top: 0, bottom: 0 },
          },
        },
        {
          content: `${item?.quantity} ${(() => {
            const unitCode = item?.isSecondaryUnit
              ? item?.unitConfig?.secondaryUnitCode
              : item?.unitConfig?.primaryUnitCode;
            return unitCode || "";
          })()}`,
          styles: { halign: "center", lineWidth: { top: 0, bottom: 0 } },
        },
        {
          content: `${item?.lineTotalSoldPrice}`,
          styles: {
            halign: "center",
            lineWidth: { right: mm(1), top: 0, bottom: 0 },
          },
        },
      ],
    ]),
    // 3. Close the bottom border on the last row
    didDrawCell: (data) => {
      if (data.row.index === data.table.body.length - 1) {
        doc.setDrawColor(0);
        doc.setLineWidth(mm(1));
        doc.line(
          data.cell.x,
          data.cell.y + data.cell.height,
          data.cell.x + data.cell.width,
          data.cell.y + data.cell.height,
        );
      }
    },
  });

  y = doc.lastAutoTable.finalY + mm(10);

  // Set dimensions for the boxes
  const boxPadding = 8;
  let boxWidth = (pageWidth - mm(24)) / 2; // Split page width minus margins
  const boxHeight = mm(35); // Height to accommodate two lines of text
  const startX = mm(8);
  const rightBoxX = startX + boxWidth + mm(8);

  // --- 1. Draw Left Box (Qty & Items) ---
  doc.setDrawColor(0); // Changed from 200 to 150 for a darker, printable gray
  doc.setLineWidth(mm(1)); // Light gray border
  doc.roundedRect(startX, y, boxWidth, boxHeight, mm(3), mm(3), "S");

  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(9);
  // Qty Line
  // doc.text("Qty :", startX + mm(5), y + mm(18));
  // doc.text(`${data.totalqty || 0}`, startX + boxWidth - mm(5), y + mm(18), { align: "right" });
  // Items Line
  doc.text("Items :", startX + mm(5), y + mm(20));
  doc.text(`${data?.totalItems}`, startX + boxWidth - mm(5), y + mm(20), {
    align: "right",
  });

  // --- 2. Draw Right Box (Total Amount) ---
  doc.roundedRect(rightBoxX, y, boxWidth, boxHeight, mm(3), mm(3), "S");

  doc.setFont("NotoSansMono-Bold", "bold").setFontSize(9);
  const totalLabel = "Grand Total";
  const totalValue = `${documentConfig?.grandTotal}`; // Assuming raw number as per UI
  const boxCenterX = rightBoxX + boxWidth / 2;

  // Draw Label: Centered horizontally
  doc.text(totalLabel, boxCenterX, y + mm(15), { align: "center" });

  doc.setFontSize(10);
  doc.text(totalValue, boxCenterX, y + mm(25), { align: "center" });

  // // Center the content within the right box
  // const combinedText = `${totalLabel} ${totalValue}`;
  // const textWidth1 = doc.getTextWidth(combinedText);
  // const textStartX = rightBoxX + (boxWidth - textWidth1) / 2;

  // doc.text(totalLabel, textStartX, y + 26);
  // doc.text(totalValue, textStartX + doc.getTextWidth(totalLabel) + 4, y + 26);

  y += boxHeight + mm(10); // Advance Y position for next section
  // y = checkPageBreak(doc, y);
  const boxMargin = mm(8);
  // 1. Prepare payment list (Only show modes with value)
  const payments = [
    {
      label: "Cash",
      amount: documentConfig?.cashPaid,
    },
    {
      label: "Card",
      amount: documentConfig?.cardPaid,
    },
    {
      label: "Online Pay",
      amount: documentConfig?.onlinePaid,
    },
    { label: "UPI", amount: documentConfig?.upiPaid },
    { label: "Balance Due", amount: documentConfig?.balanceDue },
  ].filter((p) => Math.abs(parseAmount(p.amount)) > 0);

  autoTable(doc, {
    startY: y,
    theme: "plain",
    margin: { left: boxMargin, right: boxMargin },
    styles: {
      font: "NotoSansMono-Regular",
      fontSize: 8,
      cellPadding: { top: 0.5, right: 1, bottom: 0.2, left: 1 },
      lineColor: "black",
      lineWidth: mm(1),
    },
    head:hasPayment
    ? [
      [
        {
          content: "PAYMENT MODE",
          colSpan: 3,
          styles: {
            font: "NotoSansMono-Regular",
            fontSize: 8,
            cellPadding: 1,
            fontStyle: "normal",
            lineWidth: {
              top: mm(1),
              left: mm(1),
              right: mm(1),
              bottom: mm(1),
            },
          },
        },
      ],
    ]:[],
    body: [
      ...(hasPayment
      ?payments.map((p) => [
        {
          content: p?.label,
          styles: { fontSize: 7, lineWidth: { left: mm(1) } },
        },
        { content: ":", styles: { halign: "center", lineWidth: 0 } },
        {
          content: `${(p?.amount)}`,
          styles: {
            halign: "right",
            lineWidth: { right: mm(1) },
            fontSize: 8,
          },
        },
      ]):[]),
      // Bottom Right Footer for Tender and Balance
      [
        {
          // content: `Tender: ${CurrencyConverter.toCurrency(data.cashamount || 0||0, "INR")}\nBalance: ${CurrencyConverter.toCurrency(data.cashchange || 0||0, "INR")}`,
          content: `Grand Total:   ${documentConfig?.grandTotal}`,
          colSpan: 5,
          styles: {
            halign: "right",
            font: "NotoSansMono-Bold",
            fontStyle: "bold",
            fontSize: 9,
            cellPadding: 2,
            lineWidth: {
              left: mm(1),
              right: mm(1),
              bottom: mm(1),
              top: mm(1),
            },
          },
        },
      ],
    ],
    columnStyles: {
      0: { cellWidth: "auto" },
      1: { cellWidth: "20" },
      2: { cellWidth: "auto" },
    },
  });

  y = doc.lastAutoTable.finalY + mm(15);

  const itemBoxPadding = 4;
  // y = checkPageBreak(doc, y)

  doc.setDrawColor(0);
  doc.setLineWidth(mm(1));
  const taxHead = isIGST
    ? [["Tax", "Amount", "IGST"]]
    : [["Tax", "Amount", "SGST", "CGST"]];

  const taxBody = Object.entries(taxRows)
    .filter(([percent]) => parseFloat(percent) > 0)
    .map(([percent, t]) =>
      isIGST
        ? [`${percent}%`, `${t?.basePrice}`, `${t?.igst}`]
        : [`${percent}%`, `${t?.basePrice}`, `${t?.sgst}`, `${t?.cgst}`],
    );
  // 1. Define margins and capture the starting Y position
  const taxBoxMargin = mm(8);
  const taxNotoSansMononalPadding = mm(5);
  const taxBoxWidth = pageWidth - taxBoxMargin * 2;
  const taxStartPointY = y; // Save where the box starts

  if (taxBody?.length > 0) {
    // 2. Generate the table FIRST
    autoTable(doc, {
      startY: taxStartPointY + taxNotoSansMononalPadding,
      head: [
        taxHead[0].map((cell, colIndex) => ({
          content: cell,
          styles: {
            font: "NotoSansMono-Bold",
            fontSize: 9,
            fontStyle: "bold",
            cellPadding: { top: 0.5, right: 1, bottom: 0.2, left: 1 },
            halign: colIndex === 0 ? "left" : "right",
          },
        })),
      ],
      body: taxBody?.map((row) =>
        row.map((cell, colIndex) => ({
          content: cell,
          styles: {
            font: "NotoSansMono-Regular",
            fontStyle: "normal",
            fontSize: 8,
            cellPadding: { top: 0.5, right: 1, bottom: 0.2, left: 1 },
            halign: colIndex === 0 ? "left" : "right",
          },
        })),
      ),
      theme: "plain",
      styles: {
        font: "NotoSansMono-Regular",
        fontStyle: "normal",
        fontSize: 8,
        // cellPadding: 2,
        lineWidth: 0,
      },
      margin: {
        left: taxBoxMargin + taxNotoSansMononalPadding,
        right: taxBoxMargin + taxNotoSansMononalPadding,
      },
    });

    // 3. CALCULATE HEIGHT: Use the final Y of the table
    const taxEndPointY = doc.lastAutoTable.finalY + taxNotoSansMononalPadding;
    const dynamicBoxHeight = taxEndPointY - taxStartPointY;

    // 4. DRAW THE BOX: Now we know the exact height needed
    doc.setDrawColor(0);
    doc.setLineWidth(mm(1));
    doc.rect(taxBoxMargin, taxStartPointY, taxBoxWidth, dynamicBoxHeight, "S");

    // 5. Update global Y for the next section
    y = taxEndPointY + mm(10);
  }

  //   const itemBoxPadding = 4;
  //     y = checkPageBreak(doc, y)
  //     const boxMargin = 8;

  //  const taxHead = isIGST
  //     ? [["Tax", "Amount", "IGST"]]
  //     : [["Tax", "Amount", "SGST", "CGST"]];

  //   const taxBody = Object.entries(taxRows)
  //     .filter(([percent]) => parseFloat(percent) > 0)
  //     .map(([percent, t]) =>
  //       isIGST
  //         ? [
  //             `${percent}%`,
  //             `${CurrencyConverter.toCurrency(t.basePrice || 0, "INR")}`,
  //             `${CurrencyConverter.toCurrency(t.igst || 0, "INR")}`,
  //           ]
  //         : [
  //             `${percent}%`,
  //             `${CurrencyConverter.toCurrency(t.basePrice || 0, "INR")}`,
  //             `${CurrencyConverter.toCurrency(t.sgst || 0, "INR")}`,
  //             `${CurrencyConverter.toCurrency(t.cgst || 0, "INR")}`,
  //           ]
  //     );
  //    y = checkPageBreak(doc, y)
  //         let tableRendered = false;
  //   if (taxBody.length > 0) {
  //     tableRendered = true;

  //     autoTable(doc, {
  //       startY: y,
  //       head: taxHead.map((row) =>
  //         row.map((cell, colIndex) => ({
  //           content: cell,
  //           styles: {
  //           font: "NotoSansMono-Bold",
  //                   fontSize: 14,
  //                   fontStyle: "bold",
  //               cellPadding: 2,
  //             halign: colIndex === 0 ? "left" : "right",
  //           },
  //         })),
  //       ),
  //       body: taxBody.map((row) =>
  //         row.map((cell, colIndex) => ({
  //           content: cell,
  //           styles: {
  //             font: "NotoSansMono-Regular",
  //             fontStyle: "normal",
  //              fontSize: 13,
  //             halign: colIndex === 0 ? "left" : "right",
  //           },
  //         })),
  //       ),
  //       theme: "plain",
  //       styles: {
  //         font: "NotoSansMono-Regular",
  //         fontStyle: "normal",
  //           fontSize: 12,
  //         cellPadding: 5,
  //         lineWidth: 0,
  //       },
  //       margin: { left: 10, right: 10 },
  //       columnStyles: {
  //         0: { halign: "left" },
  //         1: { halign: "right" },
  //         2: { halign: "right" },
  //         3: { halign: "right" },
  //       },
  //       didDrawCell: (data) => {
  //         const pageWidth = doc.internal.pageSize.getWidth();

  //         if (
  //           data.row.section === "head" &&
  //           data.row.index === 0 &&
  //           data.column.index === 0
  //         ) {
  //           const top = data.cell.y - 2;
  //           doc.setLineDash([1, 1], 0);
  //           doc.setDrawColor(150);
  //           doc.line(0, top, pageWidth - 5, top);
  //         }

  //         const isLastTaxRow =
  //           data.row.section === "body" &&
  //           data.row.index === taxBody.length - 1;

  //         if (isLastTaxRow && data.column.index === 0) {
  //           const bottom = data.cell.y + data.cell.height + 2;
  //           doc.setLineDash([1, 1], 0);
  //           doc.setDrawColor(150);
  //           doc.line(0, bottom, pageWidth - 5, bottom);
  //           doc.setLineDash([]);
  //         }
  //       },
  //     });
  //   }
  // if (tableRendered && doc.lastAutoTable?.finalY) {
  //     y = doc.lastAutoTable.finalY + 10;
  //   }
  // y = checkPageBreak(doc, y);
  if (parseAmount(documentConfig?.totalDiscount) > 0) {
    const boxMargin = mm(8);
    const boxHeight = mm(20);
    const boxWidth = pageWidth - boxMargin * 2;
    // o 1
    // 1. Draw the Container Box
    doc.setDrawColor(0); // Light Gray border
    doc.setLineWidth(mm(1));
    doc.rect(boxMargin, y, boxWidth, boxHeight, "S");

    // 2. Set Font for the content
    doc.setFont("NotoSansMono-Regular", "normal").setFontSize(8);

    const savedAmount = `${documentConfig?.totalDiscount}`;
    doc.text(`YOU SAVED :  ${savedAmount}`, boxMargin + mm(4), y + mm(14));

    // doc.text(savedAmount, pageWidth - (boxMargin + 4), y + 14, { align: "right" });

    y += boxHeight + mm(10);
  }
  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(11);

  // y += mm(10);
  doc.setFontSize(8);
  doc.text("Thank you for your purchase!", pageWidth / 2, y, {
    align: "center",
  });
  y += mm(10);
  //   doc.setFont("NotoSansMono-Bold", "bold");
  //   doc.text("** Visit us Again **", pageWidth / 2, y, { align: "center" });
  //   const thankBoxPadding=10;
  //  y += mm(18);

  doc.setFont("NotoSansMono-Regular", "normal");
  doc.text("Powered By", pageWidth / 2, y, { align: "center" });
  y += mm(10);
  doc.setFontSize(8);
  const sitePrefix = "www.";
  const siteBold = "NegoBill";
  const siteSuffix = ".com";
  const prefixWidth = doc.getTextWidth(sitePrefix);
  const boldWidth = doc.getTextWidth(siteBold);
  const suffixWidth = doc.getTextWidth(siteSuffix);
  const totalWidth = prefixWidth + boldWidth + suffixWidth;
  const startX1 = (pageWidth - totalWidth) / 2;
  if (storeSettings?.negobillBranding) {
    doc.setFont("NotoSansMono-Regular", "normal");
    doc.text(sitePrefix, startX1, y);
    doc.setFont("NotoSansMono-Bold", "bold");
    doc.text(siteBold, startX1 + prefixWidth, y);
    doc.setFont("NotoSansMono-Regular", "normal");
    doc.text(siteSuffix, startX1 + prefixWidth + boldWidth, y);
    y += mm(3);
  }
  // setPrint(false);
  return doc;
};

export const Generate3InchBillReceiptM2 = async (
  data,
  qrCodeDataUrl,
  terms,
  invoiceConfig,
  selectedTheme,  
  storeSettings
) => {
   const {
    documentConfig,hasPayment       
  } = invoiceConfig;
  // Conversion constant: 1mm = 2.83465pt
  const mm = (pt) => pt / 2.83465;
  const taxRows1 = documentConfig?.items;
  const itemHeight = documentConfig?.items?.length * mm(45); // Approx 45pt per item row
  const taxHeight = Object.keys(taxRows1)?.length * mm(25); //25
  const footerHeight = mm(250); // Total for branding, thank you, and payment boxes
  const headerHeight = mm(250); // Total for logo, store info, and invoice details

  // Total dynamic height with a small buffer
  const dynamicHeight = headerHeight + itemHeight + taxHeight + footerHeight;

  const doc = new jsPDF({
    orientation: "p",
    unit: "mm",
    format: [74, dynamicHeight + mm(30)],
  });

  const pageWidth = doc.internal.pageSize.getWidth();

  let y = mm(10);

  const isIGST =
    documentConfig?.fromData?.statecode &&
    documentConfig?.toData?.statecode &&
    documentConfig?.fromData.statecode !== documentConfig?.toData?.statecode;

  const taxRows = documentConfig?.items?.reduce((acc, product) => {
    const taxPercentage = product.taxpercentage || 0;
    const taxAmount = product.lineTotalTaxAmount || 0;
    const basePrice = product.lineTotalBasePrice || 0;
    if (!acc[taxPercentage]) {
      acc[taxPercentage] = { basePrice: 0, sgst: 0, cgst: 0, igst: 0 };
    }
    acc[taxPercentage].basePrice += basePrice;
    if (isIGST) {
      acc[taxPercentage].igst += taxAmount;
    } else {
      acc[taxPercentage].sgst += taxAmount / 2;
      acc[taxPercentage].cgst += taxAmount / 2;
    }
    return acc;
  }, {});
  const checkPageBreak = (doc, y, buffer = mm(20)) => {
    const pageHeight = doc.internal.pageSize.getHeight();
    if (y + buffer >= pageHeight) {
      doc.addPage();
      return mm(14);
    }
    return y;
  };
  // const storeBoxStartY = y;

  const imagePath = data.imagePath;

  // const imagePath = settings?.saleProfileData?.image
  //   ?  settings?.saleProfileData?.image
  //   : "/assets/images/logo-light.png";

  if (documentConfig?.image) {
    try {
      // Step 1: Convert image URL to base64
      const response = await fetch(imagePath, { mode: "cors" });
      const blob = await response.blob();
      let base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

      // Step 2: Convert to grayscale
      base64 = await convertToGrayScale(base64);

      // Step 3: Get natural dimensions
      const { width: naturalWidth, height: naturalHeight } =
        await loadImageDimensions(base64);

      // Step 4: Resize to fit dynamically within maxWidth and maxHeight
      const maxWidth = mm(80); // maximum width in pt for 3-inch receipt
      const maxHeight = mm(40); // maximum height in pt
      const widthRatio = maxWidth / naturalWidth;
      const heightRatio = maxHeight / naturalHeight;
      const ratio = Math.min(widthRatio, heightRatio, 1); // don't upscale

      const imgWidth = naturalWidth * ratio;
      const imgHeight = naturalHeight * ratio;

      // Step 5: Center the logo
      const logoX = (pageWidth - imgWidth) / 2;

      doc.addImage(base64, "PNG", logoX, y, imgWidth, imgHeight);

      y += imgHeight + mm(10); // spacing after logo
    } catch (err) {
      console.warn("Logo failed to load:", err);
    }
  }

  let margin = mm(15);
  const drawDashedLine = (yPos) => {
    doc.setLineDash([mm(3), mm(3)], 0);
    doc.line(margin, yPos, pageWidth - margin, yPos);
    doc.setLineDash([]); // Reset to solid
  };
  // Header

  doc.setFont("NotoSansMono-Bold", "bold");

  doc.setFontSize(11);
  const storeTitle =   documentConfig?.fromData?.title || "Store";
  doc.splitTextToSize(storeTitle, pageWidth - mm(30)).forEach((line) => {
    doc.text(line, pageWidth / 2, y, { align: "center" });
    y += mm(13);
  });
  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(8);
  const seller = documentConfig?.fromData;
  const sellerAddressParts = [
    seller?.addressline1,
    [seller?.state].filter(Boolean).join(", "),
  ].filter(Boolean);
  doc
    .splitTextToSize(sellerAddressParts.join(", "), pageWidth - mm(50))
    .forEach((line) => {
      doc.text(line, pageWidth / 2, y, { align: "center" });
      y += mm(8);
    });

  // if (seller?.email) {
  //   doc.text(`Email: ${seller.email}`, pageWidth / 2, y, { align: "center" });
  //   y += 16;
  // }
  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(8); // Slightly smaller to match UI

  const addressText = `${seller?.district} ${seller?.country} - ${seller?.pincode}`;
  doc.text(addressText, pageWidth / 2, y, { align: "center" });
  y += mm(8);
  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(8);
  // const gstText = `GST: ${seller?.gstnumber || ""}`;
    const phoneNumbers = [
  documentConfig?.fromData?.phone,
  documentConfig?.fromData?.additionalPhone,
].filter(Boolean);
 const phoneText = phoneNumbers.length
  ? `Ph: ${phoneNumbers.join(", ")}`
  : "";
  const fssainumber = `FSSAI : ${seller?.fssai || ""}`;
  const pannumber = `PAN : ${seller?.pannumber || ""}`;
  const cinnumber = `CIN :${seller?.cinnumber || ""}`;

  const horizontalPadding = mm(12);
  const leftStart = horizontalPadding;
  const rightEnd = pageWidth - horizontalPadding;
  // Draw GST: Aligned to the far left margin

  doc.text(pannumber, leftStart, y, { align: "left" });
  // Draw Phone: Aligned to the far right margin
  // doc.text(phoneText, rightEnd, y, { align: "right" });
  if (phoneText) {
  const phoneMaxWidth = pageWidth / 2 - horizontalPadding;

  const phoneLines = doc.splitTextToSize(
    phoneText,
    phoneMaxWidth
  );

  doc.text(phoneLines, rightEnd, y, {
    align: "right",
  });

  // Move down based on wrapped lines
  y += phoneLines.length * 4;
} else {
  y += 4;
}
  // y += mm(2);
  {
    seller?.fssai && doc.text(fssainumber, leftStart, y, { align: "left" });
    y += mm(8);
  }

  // Draw Phone: Aligned to the far right margin
  {
    seller?.cinnumber && doc.text(cinnumber, leftStart, y, { align: "left" });
  }
  y += mm(12);
  // drawDashedLine(y);
  const storeBoxPadding = 4;

  doc.setLineWidth(mm(1));
  doc.setLineDash([mm(1), mm(1)]); // solid border

  // y += mm(8);
  const invoiceTitle =
    data?.status === "CANCELLED"
      ? "CANCELLED TAX INVOICE"
      :documentConfig?.title;

  doc.setFont("NotoSansMono-Bold", "bold").setFontSize(11);
  const textWidth = doc.getTextWidth(invoiceTitle);
  const gap = mm(8); // Space between the line and the text
  margin = mm(10);
  // 5. Draw the Lines
  let centerX = pageWidth / 2;
  doc.text(invoiceTitle, centerX, y, { align: "center" });
  doc.setLineWidth(mm(1));
  doc.setLineDash([mm(3), mm(3)]); // Use [0] for a solid line, [2, 2] for dashed like your image

  // Left Line: starts at margin, ends just before the text
  doc.line(margin, y - mm(4), centerX - textWidth / 2 - gap, y - mm(4));

  // Right Line: starts just after the text, ends at margin
  doc.line(
    centerX + textWidth / 2 + gap,
    y - mm(4),
    pageWidth - margin,
    y - mm(4),
  );
  // 6. Reset Dash and move Y
  doc.setLineDash([]);
  y += mm(10);

  const { date, time } = documentConfig?.Date;
  const lineHeight = mm(9); // Adjusted for tighter spacing seen in UI
  const leftColX = mm(8); // Left margin
  const rightColX = pageWidth / 2 + mm(10); // Start of the second column
  const drawField = (label, value, xPos, yPos) => {
    doc.setFont("NotoSansMono-Regular", "normal").setFontSize(9);
    const fieldText = `${label} : ${value}`;
    doc.text(fieldText, xPos, yPos);
  };
  drawField( documentConfig?.numberlabel, `${ documentConfig?.number}`, leftColX, y);
  // drawField("Counter", "02", rightColX, y); // Replace "02" with your dynamic counter data?
  y += lineHeight;

  // Row 2: Date
  drawField("Date", `${date}, ${time}`, leftColX, y);
  y += lineHeight;

  // Row 3: Cashier
  // drawField("Cashier", "Stejo", leftColX, y); // Replace "Stejo" with your dynamic cashier data

  const labelX = 8; // Left-aligned label start
  const colonX = 110; // Colon aligned at this point
  const valueX = 120; // Value starts after colon

  y += mm(6);

  const buyer = documentConfig?.toData;
  const hasBuyerInfo =
    buyer &&
    (buyer?.name ||
      buyer?.phone ||
      buyer?.addressline1 ||
      buyer?.email ||
      buyer?.gstnumber ||
      buyer?.pannumber ||
      buyer?.cinnumber);

  // --- BUYER INFORMATION SECTION ---
  if (hasBuyerInfo) {
    // 1. Section Header: Clean and Centered
    doc.setFont("NotoSansMono-Regular", "normal").setFontSize(9);
    doc.text("BUYER INFORMATION", pageWidth / 2, y, { align: "center" });
    y += mm(10);

    const leftMargin = mm(8);
    const lineHeight = mm(9);

    //   // 2. Buyer Name: Bold and Uppercase for Impact
    if (buyer?.name) {
      doc.setFont("NotoSansMono-Bold", "bold").setFontSize(8);
      doc.text(buyer?.name.toUpperCase(), leftMargin, y);
      y += lineHeight;
    }

    // 3. Buyer Details: Smaller and Regular font
    doc.setFont("NotoSansMono-Regular", "normal").setFontSize(8);

    // Contact Row (Phone & Email)
    if (buyer?.phone || buyer?.email) {
      const contactLine = [
        buyer?.phone ? `Ph: ${buyer?.phone}` : null,
        buyer?.email ? `Email: ${buyer?.email}` : null,
      ]
        .filter(Boolean)
        .join(" | ");

      doc.text(contactLine, leftMargin, y);
      y += lineHeight;
    }

    // Address: Handling long text with splitTextToSize
    const addressParts = [
      buyer?.addressline1,
      [buyer?.state, buyer?.district, buyer?.country]
        .filter(Boolean)
        .join(", "),
      buyer?.pincode ? `- ${buyer?.pincode}` : null,
    ]
      .filter(Boolean)
      .join(", ");

    if (addressParts) {
      const splitAddress = doc.splitTextToSize(
        addressParts,
        pageWidth - mm(20),
      );
      splitAddress.forEach((line) => {
        doc.text(line, leftMargin, y);
        y += lineHeight;
      });
    }

    // 4. Statutory IDs (GST, PAN, CIN): Compact layout
     const idList = [
      { label: "GST: ", value: buyer.gstnumber, bold: true,},      
      { label: "PAN: ", value: buyer.pannumber ,bold: true,},
      { label: "CIN: ", value: buyer.cinnumber,bold: true, },
      { label: "FSSAI: ", value: buyer.fssai,bold: true, },
      { label: "Reference No: ", value: buyer.referenceid, bold: false, },
    ];
    idList.forEach((item) => {
      if (item?.value) {
        const leftMargin = mm(8);
if (item.bold) {
      doc.setFont("NotoSansMono-Bold", "bold");
      } else {
        doc.setFont("NotoSansMono-Regular", "normal");
      }  
        // 1. Draw the Label in BOLD
        doc.setFontSize(8);
        doc.text(item?.label, leftMargin, y);

        // 2. Calculate label width so the value starts exactly after it
        const labelWidth = doc.getTextWidth(item?.label);

        // 3. Draw the Value in REGULAR
        doc.setFont("NotoSansMono-Regular", "normal");
        doc.text(`${item?.value}`, leftMargin + labelWidth, y);

        // 4. Move to next line
        y += mm(9);
      }
    });

    y += mm(3); // Extra spacing before the items table starts
  }

  // Define table boundaries
  const tableMargin = mm(8);
  const tableWidth = pageWidth - tableMargin * 2;

  autoTable(doc, {
    startY: y,
    margin: { left: tableMargin, right: tableMargin },
    theme: "plain",
    // 1. Set global border styles
    styles: {
      fontSize: 9,
      cellPadding: { top: 0.5, right: 1, bottom: 0.2, left: 1 },
      lineColor: "black", // Light gray border
      lineWidth: 1,
      font: "NotoSansMono-Regular",
    },
    head: [
      // Header Row 1: "Items" label span
      [
        {
          content: "Items",
          cellPadding: 1,
          colSpan: 3,
          styles: {
            halign: "left",
            fontStyle: "bold",
            lineWidth: { left: mm(1), right: mm(1), top: mm(1), bottom: 0 },
          },
        },
      ],
      // Header Row 2: Rate, Qty, Total
      [
        {
          content: "Rate",
          cellPadding: 1,
          styles: {
            halign: "center",
            fontStyle: "bold",
            lineWidth: { left: mm(1), top: 0, bottom: mm(1) },
          },
        },
        {
          content: "Qty",
          cellPadding: 1,
          styles: {
            halign: "center",
            fontStyle: "bold",
            lineWidth: { top: 0, bottom: mm(1) },
          },
        },
        {
          content: "Total",
          cellPadding: 1,
          styles: {
            halign: "center",
            fontStyle: "bold",
            lineWidth: { right: mm(1), top: 0, bottom: mm(1) },
          },
        },
      ],
    ],
    body: documentConfig?.items.flatMap((item) => [
      // Line 1: Item Name (Full width)
      [
        {
          content: item?.title + (item?.note ? ` (${item?.note})` : ""),
          colSpan: 3,
          styles: {
            halign: "left",
            lineWidth: { left: mm(1), right: mm(1), top: 0, bottom: 0 },
          },
        },
      ],
      // Line 2: Values (Rate, Qty, Total)
      [
        {
          content: `${item?.soldprice}`,
          styles: {
            halign: "center",
            lineWidth: { left: mm(1), top: 0, bottom: 0 },
          },
        },
        {
          content: `${item?.quantity} ${(() => {
            const unitCode = item?.isSecondaryUnit
              ? item?.unitConfig?.secondaryUnitCode
              : item?.unitConfig?.primaryUnitCode;
            return unitCode || "";
          })()}`,
          styles: { halign: "center", lineWidth: { top: 0, bottom: 0 } },
        },
        {
          content: `${item?.lineTotalSoldPrice}`,
          styles: {
            halign: "center",
            lineWidth: { right: mm(1), top: 0, bottom: 0 },
          },
        },
      ],
    ]),
    // 3. Close the bottom border on the last row
    didDrawCell: (data) => {
      if (data.row.index === data.table.body.length - 1) {
        doc.setDrawColor(0);
        doc.setLineWidth(mm(1));
        doc.line(
          data.cell.x,
          data.cell.y + data.cell.height,
          data.cell.x + data.cell.width,
          data.cell.y + data.cell.height,
        );
      }
    },
  });

  y = doc.lastAutoTable.finalY + mm(10);

  // Set dimensions for the boxes
  const boxPadding = 8;
  let boxWidth = (pageWidth - mm(24)) / 2; // Split page width minus margins
  const boxHeight = mm(45); // Height to accommodate two lines of text
  const startX = mm(8);
  const rightBoxX = startX + boxWidth + mm(8);

  // --- 1. Draw Left Box (Qty & Items) ---
  doc.setDrawColor(0); // Changed from 200 to 150 for a darker, printable gray
  doc.setLineWidth(mm(1)); // Light gray border
  doc.roundedRect(startX, y, boxWidth, boxHeight, mm(3), mm(3), "S");

  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(9);
  // Qty Line
  // doc.text("Qty :", startX + mm(5), y + mm(18));
  // doc.text(`${data.totalqty || 0}`, startX + boxWidth - mm(5), y + mm(18), { align: "right" });
  // Items Line
  doc.text("Items :", startX + mm(5), y + mm(27));
  doc.text(`${data?.totalItems}`, startX + boxWidth - mm(5), y + mm(27), {
    align: "right",
  });

  // --- 2. Draw Right Box (Total Amount) ---
  doc.roundedRect(rightBoxX, y, boxWidth, boxHeight, mm(3), mm(3), "S");

  doc.setFont("NotoSansMono-Bold", "bold").setFontSize(9);
  const totalLabel = "Grand Total";
  const totalValue = `${documentConfig?.grandTotal}`; // Assuming raw number as per UI
  const boxCenterX = rightBoxX + boxWidth / 2;

  // Draw Label: Centered horizontally
  doc.text(totalLabel, boxCenterX, y + mm(18), { align: "center" });

  doc.setFontSize(10);
  doc.text(totalValue, boxCenterX, y + mm(38), { align: "center" });

  // // Center the content within the right box
  // const combinedText = `${totalLabel} ${totalValue}`;
  // const textWidth1 = doc.getTextWidth(combinedText);
  // const textStartX = rightBoxX + (boxWidth - textWidth1) / 2;

  // doc.text(totalLabel, textStartX, y + 26);
  // doc.text(totalValue, textStartX + doc.getTextWidth(totalLabel) + 4, y + 26);

  y += boxHeight + mm(10); // Advance Y position for next section

  // y = checkPageBreak(doc, y);
  const boxMargin = mm(8);
  // 1. Prepare payment list (Only show modes with value)
  const payments = [
    {
      label: "Cash",
      amount: documentConfig?.cashPaid,
    },
    {
      label: "Card",
      amount: documentConfig?.cardPaid,
    },
    {
      label: "Online Pay",
      amount: documentConfig?.onlinePaid,
    },
    { label: "UPI", amount: documentConfig?.upiPaid },
    { label: "Balance Due", amount: documentConfig?.balanceDue },
  ].filter((p) => Math.abs(parseAmount(p.amount)) > 0);

  autoTable(doc, {
    startY: y,
    theme: "plain",
    margin: { left: boxMargin, right: boxMargin },
    styles: {
      font: "NotoSansMono-Regular",
      fontSize: 8,
      cellPadding: { top: 0.5, right: 1, bottom: 0.2, left: 1 },
      lineColor: "black",
      lineWidth: mm(1),
    },
    head:hasPayment
    ?  [
      [
        {
          content: "PAYMENT MODE",
          colSpan: 3,
          styles: {
            font: "NotoSansMono-Regular",
            fontSize: 8,
            cellPadding: 1,
            fontStyle: "normal",
            lineWidth: {
              top: mm(1),
              left: mm(1),
              right: mm(1),
              bottom: mm(1),
            },
          },
        },
      ],
    ]:[],
    body: [
     ...(hasPayment
      ?payments?.map((p) => [
        {
          content: p?.label,
          styles: { fontSize: 7, lineWidth: { left: mm(1) } },
        },
        { content: ":", styles: { halign: "center", lineWidth: 0 } },
        {
          content: `${(p?.amount)}`,
          styles: {
            halign: "right",
            lineWidth: { right: mm(1) },
            fontSize: 8,
          },
        },
      ]):[]),
      // Bottom Right Footer for Tender and Balance
      [
        {
          // content: `Tender: ${CurrencyConverter.toCurrency(data.cashamount || 0||0, "INR")}\nBalance: ${CurrencyConverter.toCurrency(data.cashchange || 0||0, "INR")}`,
          content: `Grand Total: ${documentConfig?.grandTotal}`,
          colSpan: 5,
          styles: {
            halign: "right",
            font: "NotoSansMono-Bold",
            fontStyle: "bold",
            fontSize: 9,
            cellPadding: 2,
            lineWidth: {
              left: mm(1),
              right: mm(1),
              bottom: mm(1),
              top: mm(1),
            },
          },
        },
      ],
    ],
    columnStyles: {
      0: { cellWidth: "auto" },
      1: { cellWidth: "20" },
      2: { cellWidth: "auto" },
    },
  });

  y = doc.lastAutoTable.finalY + mm(15);

  const itemBoxPadding = 4;
  // y = checkPageBreak(doc, y)

  //  doc.setDrawColor(0);
  // doc.setLineWidth(mm(1));
  //     const taxHead = isIGST
  //       ? [["Tax", "Amount", "IGST"]]
  //       : [["Tax", "Amount", "SGST", "CGST"]];

  //     const taxBody = Object.entries(taxRows)
  //       .filter(([percent]) => parseFloat(percent) > 0)
  //       .map(([percent, t]) =>
  //         isIGST
  //           ? [
  //               `${percent}%`,
  //               `${CurrencyConverter.toCurrency(t.basePrice || 0, "INR")}`,
  //               `${CurrencyConverter.toCurrency(t.igst || 0, "INR")}`,
  //             ]
  //           : [
  //               `${percent}%`,
  //               `${CurrencyConverter.toCurrency(t.basePrice || 0, "INR")}`,
  //               `${CurrencyConverter.toCurrency(t.sgst || 0, "INR")}`,
  //               `${CurrencyConverter.toCurrency(t.cgst || 0, "INR")}`,
  //             ]
  //       );
  // 1. Define margins and capture the starting Y position
  // const taxBoxMargin = mm(8);
  // const taxNotoSansMononalPadding = mm(5);
  // const taxBoxWidth = pageWidth - (taxBoxMargin * 2);
  // const taxStartPointY = y; // Save where the box starts

  // if (taxBody.length > 0) {
  //     // 2. Generate the table FIRST
  //     autoTable(doc, {
  //         startY: taxStartPointY + taxNotoSansMononalPadding,
  //         head: [
  //             taxHead[0].map((cell, colIndex) => ({
  //                 content: cell,
  //                 styles: {
  //                     font: "NotoSansMono-Bold",
  //                     fontSize: 9,
  //                     fontStyle: "bold",
  //                     cellPadding: { top: 0.5, right: 1, bottom: 0.2, left: 1 },
  //                     halign: colIndex === 0 ? "left" : "right",
  //                 },
  //             })),
  //         ],
  //         body: taxBody.map((row) =>
  //             row.map((cell, colIndex) => ({
  //                 content: cell,
  //                 styles: {
  //                     font: "NotoSansMono-Regular",
  //                     fontStyle: "normal",
  //                     fontSize: 8,
  //                     cellPadding: { top: 0.5, right: 1, bottom: 0.2, left: 1 },
  //                     halign: colIndex === 0 ? "left" : "right",
  //                 },
  //             }))
  //         ),
  //         theme: "plain",
  //            styles: {
  //           font: "NotoSansMono-Regular",
  //           fontStyle: "normal",
  //           fontSize: 8,
  //           // cellPadding: 2,
  //           lineWidth: 0,
  //         },
  //         margin: {
  //             left: taxBoxMargin + taxNotoSansMononalPadding,
  //             right: taxBoxMargin + taxNotoSansMononalPadding
  //         },
  //     });

  //     // 3. CALCULATE HEIGHT: Use the final Y of the table
  //     const taxEndPointY = doc.lastAutoTable.finalY + taxNotoSansMononalPadding;
  //     const dynamicBoxHeight = taxEndPointY - taxStartPointY;

  //     // 4. DRAW THE BOX: Now we know the exact height needed
  //     doc.setDrawColor(0);
  //     doc.setLineWidth(mm(1));
  //     doc.rect(taxBoxMargin, taxStartPointY, taxBoxWidth, dynamicBoxHeight, 'S');

  //     // 5. Update global Y for the next section
  //     y = taxEndPointY + mm(10);
  // }

  //   const itemBoxPadding = 4;
  //     y = checkPageBreak(doc, y)
  //     const boxMargin = 8;

  //  const taxHead = isIGST
  //     ? [["Tax", "Amount", "IGST"]]
  //     : [["Tax", "Amount", "SGST", "CGST"]];

  //   const taxBody = Object.entries(taxRows)
  //     .filter(([percent]) => parseFloat(percent) > 0)
  //     .map(([percent, t]) =>
  //       isIGST
  //         ? [
  //             `${percent}%`,
  //             `${CurrencyConverter.toCurrency(t.basePrice || 0, "INR")}`,
  //             `${CurrencyConverter.toCurrency(t.igst || 0, "INR")}`,
  //           ]
  //         : [
  //             `${percent}%`,
  //             `${CurrencyConverter.toCurrency(t.basePrice || 0, "INR")}`,
  //             `${CurrencyConverter.toCurrency(t.sgst || 0, "INR")}`,
  //             `${CurrencyConverter.toCurrency(t.cgst || 0, "INR")}`,
  //           ]
  //     );
  //    y = checkPageBreak(doc, y)
  //         let tableRendered = false;
  //   if (taxBody.length > 0) {
  //     tableRendered = true;

  //     autoTable(doc, {
  //       startY: y,
  //       head: taxHead.map((row) =>
  //         row.map((cell, colIndex) => ({
  //           content: cell,
  //           styles: {
  //           font: "NotoSansMono-Bold",
  //                   fontSize: 14,
  //                   fontStyle: "bold",
  //               cellPadding: 2,
  //             halign: colIndex === 0 ? "left" : "right",
  //           },
  //         })),
  //       ),
  //       body: taxBody.map((row) =>
  //         row.map((cell, colIndex) => ({
  //           content: cell,
  //           styles: {
  //             font: "NotoSansMono-Regular",
  //             fontStyle: "normal",
  //              fontSize: 13,
  //             halign: colIndex === 0 ? "left" : "right",
  //           },
  //         })),
  //       ),
  //       theme: "plain",
  //       styles: {
  //         font: "NotoSansMono-Regular",
  //         fontStyle: "normal",
  //           fontSize: 12,
  //         cellPadding: 5,
  //         lineWidth: 0,
  //       },
  //       margin: { left: 10, right: 10 },
  //       columnStyles: {
  //         0: { halign: "left" },
  //         1: { halign: "right" },
  //         2: { halign: "right" },
  //         3: { halign: "right" },
  //       },
  //       didDrawCell: (data) => {
  //         const pageWidth = doc.internal.pageSize.getWidth();

  //         if (
  //           data.row.section === "head" &&
  //           data.row.index === 0 &&
  //           data.column.index === 0
  //         ) {
  //           const top = data.cell.y - 2;
  //           doc.setLineDash([1, 1], 0);
  //           doc.setDrawColor(150);
  //           doc.line(0, top, pageWidth - 5, top);
  //         }

  //         const isLastTaxRow =
  //           data.row.section === "body" &&
  //           data.row.index === taxBody.length - 1;

  //         if (isLastTaxRow && data.column.index === 0) {
  //           const bottom = data.cell.y + data.cell.height + 2;
  //           doc.setLineDash([1, 1], 0);
  //           doc.setDrawColor(150);
  //           doc.line(0, bottom, pageWidth - 5, bottom);
  //           doc.setLineDash([]);
  //         }
  //       },
  //     });
  //   }
  // if (tableRendered && doc.lastAutoTable?.finalY) {
  //     y = doc.lastAutoTable.finalY + 10;
  //   }
  // y = checkPageBreak(doc, y);
  if (parseAmount(documentConfig?.totalDiscount) > 0) {
    const boxMargin = mm(8);
    const boxHeight = mm(20);
    const boxWidth = pageWidth - boxMargin * 2;
    // o 1
    // 1. Draw the Container Box
    doc.setDrawColor(0); // Light Gray border
    doc.setLineWidth(mm(1));
    doc.rect(boxMargin, y, boxWidth, boxHeight, "S");

    // 2. Set Font for the content
    doc.setFont("NotoSansMono-Regular", "normal").setFontSize(8);

    const savedAmount = `${documentConfig?.totalDiscount}`;
    doc.text(`YOU SAVED :  ${savedAmount}`, boxMargin + mm(4), y + mm(14));

    // doc.text(savedAmount, pageWidth - (boxMargin + 4), y + 14, { align: "right" });

    y += boxHeight + mm(10);
  }
  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(11);

  // y += mm(10);
  doc.setFontSize(8);
  doc.text("Thank you for your purchase!", pageWidth / 2, y, {
    align: "center",
  });
  y += mm(10);
  //   doc.setFont("NotoSansMono-Bold", "bold");
  //   doc.text("** Visit us Again **", pageWidth / 2, y, { align: "center" });
  //   const thankBoxPadding=10;
  //  y += mm(18);

  doc.setFont("NotoSansMono-Regular", "normal");
  doc.text("Powered By", pageWidth / 2, y, { align: "center" });
  y += mm(10);
  doc.setFontSize(8);
  const sitePrefix = "www.";
  const siteBold = "NegoBill";
  const siteSuffix = ".com";
  const prefixWidth = doc.getTextWidth(sitePrefix);
  const boldWidth = doc.getTextWidth(siteBold);
  const suffixWidth = doc.getTextWidth(siteSuffix);
  const totalWidth = prefixWidth + boldWidth + suffixWidth;
  const startX1 = (pageWidth - totalWidth) / 2;
  if (storeSettings?.negobillBranding) {
    doc.setFont("NotoSansMono-Regular", "normal");
    doc.text(sitePrefix, startX1, y);
    doc.setFont("NotoSansMono-Bold", "bold");
    doc.text(siteBold, startX1 + prefixWidth, y);
    doc.setFont("NotoSansMono-Regular", "normal");
    doc.text(siteSuffix, startX1 + prefixWidth + boldWidth, y);
    y += mm(3);
  }
  // setPrint(false);
  return doc;
};

export const Generate2InchInvoiceReceiptM2 = async (
 data,
  qrCodeDataUrl,
  terms,
  invoiceConfig,
  selectedTheme,  
  storeSettings
) => {
  const {
    documentConfig, hasPayment      
  } = invoiceConfig;
 
  const mm = (pt) => pt / 2.83465;

  const itemHeight = documentConfig?.items?.length * mm(45);
  const taxHeight = Object.keys(documentConfig?.items)?.length * mm(25);
  const footerHeight = mm(250);
  const headerHeight = mm(250);
  const dynamicHeight = headerHeight + itemHeight + taxHeight + footerHeight;

  const doc = new jsPDF({
    orientation: "p",
    unit: "mm",
    format: [49, dynamicHeight + mm(30)],
  });
  const pageWidth = doc.internal.pageSize.getWidth();

  let y = mm(10);

  const isIGST =
    documentConfig?.fromData?.statecode &&
    documentConfig?.toData?.statecode &&
    documentConfig?.fromData.statecode !== documentConfig?.toData?.statecode;

  const taxRows = documentConfig?.items?.reduce((acc, product) => {
    const taxPercentage = product.taxpercentage || 0;
    const taxAmount = product.lineTotalTaxAmount || 0;
    const basePrice = product.lineTotalBasePrice || 0;
    if (!acc[taxPercentage]) {
      acc[taxPercentage] = { basePrice: 0, sgst: 0, cgst: 0, igst: 0 };
    }
    acc[taxPercentage].basePrice += basePrice;
    if (isIGST) {
      acc[taxPercentage].igst += taxAmount;
    } else {
      acc[taxPercentage].sgst += taxAmount / 2;
      acc[taxPercentage].cgst += taxAmount / 2;
    }
    return acc;
  }, {});
  const checkPageBreak = (doc, y, buffer = 20) => {
    const pageHeight = doc.internal.pageSize.getHeight();
    if (y + buffer >= pageHeight) {
      doc.addPage();
      return 14;
    }
    return y;
  };
  // const storeBoxStartY = y;

   const imagePath =  documentConfig?.image;


  // const imagePath = settings?.saleProfileData?.image
  //   ?  settings?.saleProfileData?.image
  //   : "/assets/images/logo-light.png";

  if (documentConfig?.image) {
    try {
      // Step 1: Convert image URL to base64
      const response = await fetch(imagePath, { mode: "cors" });
      const blob = await response.blob();
      let base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

      // Step 2: Convert to grayscale
      base64 = await convertToGrayScale(base64);

      // Step 3: Get natural dimensions
      const { width: naturalWidth, height: naturalHeight } =
        await loadImageDimensions(base64);

      // Step 4: Resize to fit dynamically within maxWidth and maxHeight
      const maxWidth = mm(50); // maximum width in pt for 3-inch receipt
      const maxHeight = mm(40); // maximum height in pt
      const widthRatio = maxWidth / naturalWidth;
      const heightRatio = maxHeight / naturalHeight;
      const ratio = Math.min(widthRatio, heightRatio, 1); // don't upscale

      const imgWidth = naturalWidth * ratio;
      const imgHeight = naturalHeight * ratio;

      // Step 5: Center the logo
      const logoX = (pageWidth - imgWidth) / 2;

      doc.addImage(base64, "PNG", logoX, y, imgWidth, imgHeight);

      y += imgHeight + mm(10); // spacing after logo
    } catch (err) {
      console.warn("Logo failed to load:", err);
    }
  }

  let margin = mm(6);

  // Header

  doc.setFont("NotoSansMono-Bold", "bold");

  doc.setFontSize(6);
  const storeTitle =  documentConfig?.fromData?.title || "Store";
  doc.splitTextToSize(storeTitle, pageWidth - mm(30)).forEach((line) => {
    doc.text(line, pageWidth / 2, y, { align: "center" });
    y += mm(7);
  });
  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(6);
  const seller = documentConfig?.fromData;
  const sellerAddressParts = [
    seller?.addressline1,
    [seller?.state].filter(Boolean).join(", "),
  ].filter(Boolean);
  doc
    .splitTextToSize(sellerAddressParts.join(", "), pageWidth - mm(50))
    .forEach((line) => {
      doc.text(line, pageWidth / 2, y, { align: "center" });
      y += mm(7);
    });

  // if (seller?.email) {
  //   doc.text(`Email: ${seller.email}`, pageWidth / 2, y, { align: "center" });
  //   y += 16;
  // }
  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(6); // Slightly smaller to match UI

  const addressText = `${seller?.district} ${seller?.country} - ${seller?.pincode}`;
  doc.text(addressText, pageWidth / 2, y, { align: "center" });
  y += mm(7);
  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(6);
  const gstText = `GST: ${seller?.gstnumber || ""}`;
   const phoneNumbers = [
  documentConfig?.fromData?.phone,
  documentConfig?.fromData?.additionalPhone,
].filter(Boolean);
 const phoneText = phoneNumbers.length
  ? `Ph: ${phoneNumbers.join(", ")}`
  : "";

  const horizontalPadding = mm(6);
  const leftStart = horizontalPadding;
  const rightEnd = pageWidth - horizontalPadding;
 if (phoneText) {
  const phoneMaxWidth = pageWidth - mm(20);

  const phoneLines = doc.splitTextToSize(
    phoneText,
    phoneMaxWidth,
  );

  doc.text(phoneLines, pageWidth / 2, y, {
    align: "center",
  });

  // Move according to actual phone lines
  y += phoneLines.length * mm(4) +2;
}
  doc.text(gstText, leftStart, y, { align: "left" });
  // doc.text(phoneText, rightEnd, y, { align: "right" });
  y += mm(7);

  const fassiTest = `FSSAI:${seller?.fssai || ""}`;
  const panTex = `PAN:${seller?.pannumber || ""}`;
  if (seller?.fssai) {
    doc.setFont("NotoSansMono-Regular", "normal").setFontSize(6);
    doc.text(fassiTest, rightEnd, y, { align: "right" });
    // y += mm(7);
  }
  // if (seller?.pannumber) {
  doc.text(panTex, leftStart, y, { align: "left" });
  y += mm(7);
  // }
  if (seller?.cinnumber) {
    const cinTest = `CIN: ${seller?.cinnumber || ""}`;

    doc.setFont("NotoSansMono-Regular", "normal").setFontSize(6);
    doc.text(cinTest, leftStart, y, { align: "left" });
    y += mm(7);
  }
  const storeBoxPadding = 4;

  doc.setLineWidth(mm(1));
  doc.setLineDash([mm(1), mm(1)]); // solid border

  const invoiceTitle =
    data?.status === "CANCELLED"
      ? "CANCELLED TAX INVOICE"
     :documentConfig?.title;

  doc.setFont("NotoSansMono-Bold", "bold").setFontSize(7);
  const textWidth = doc.getTextWidth(invoiceTitle);
  const gap = mm(8); // Space between the line and the text
  margin = mm(10);
  // 5. Draw the Lines
  let centerX = pageWidth / 2;
  y += mm(7);
  doc.text(invoiceTitle, centerX, y, { align: "center" });
  doc.setLineWidth(mm(1));
  doc.setLineDash([mm(3), mm(3)]); // Use [0] for a solid line, [2, 2] for dashed like your image

  // Left Line: starts at margin, ends just before the text
  doc.line(margin, y - mm(4), centerX - textWidth / 2 - gap, y - mm(4));

  // Right Line: starts just after the text, ends at margin
  doc.line(
    centerX + textWidth / 2 + gap,
    y - mm(4),
    pageWidth - margin,
    y - mm(4),
  );
  doc.setLineDash([]);
  y += mm(10);

  const { date, time } = documentConfig?.Date;
  const lineHeight = mm(7); // Adjusted for tighter spacing seen in UI
  const leftColX = mm(8); // Left margin
  const rightColX = pageWidth / 2 + 10; // Start of the second column
  const drawField = (label, value, xPos, yPos) => {
    doc.setFont("NotoSansMono-Regular", "normal").setFontSize(7);
    const fieldText = `${label} : ${value}`;
    doc.text(fieldText, xPos, yPos);
  };
  drawField(
   documentConfig?.numberLabel ,
    `${documentConfig?.number}`,   
    leftColX,
    y,
  );
  // drawField("Counter", "02", rightColX, y); // Replace "02" with your dynamic counter data
  y += lineHeight;

  // Row 2: Date
  drawField("Date", `${date}, ${time}`, leftColX, y);
  y += lineHeight;

  // Row 3: Cashier
  // drawField("Cashier", "Stejo", leftColX, y); // Replace "Stejo" with your dynamic cashier data
  y += mm(5);
  // buyer info

  const buyer = documentConfig?.toData;
  const hasBuyerInfo =
    buyer &&
    (buyer?.name ||
      buyer?.phone ||
      buyer?.addressline1 ||
      buyer?.email ||
      buyer?.gstnumber ||
      buyer?.pannumber ||
      buyer?.cinnumber);
  if (hasBuyerInfo) {
    doc.setFont("NotoSansMono-Bold", "bold").setFontSize(7);
    doc.text("BUYER INFORMATION", pageWidth / 2, y, { align: "center" });
    y += mm(8);
    const leftMargin = mm(8);
    const lineHeight = mm(7);
    if (buyer?.name) {
      doc.setFont("NotoSansMono-Bold", "bold").setFontSize(6);
      doc.text(buyer?.name.toUpperCase(), leftMargin, y);
      y += lineHeight;
    }
    doc.setFont("NotoSansMono-Regular", "normal").setFontSize(6);
    if (buyer?.phone || buyer?.email) {
      const contactLine = [
        buyer?.phone ? `Ph: ${buyer?.phone}` : null,
        buyer?.email ? `Email: ${buyer?.email}` : null,
      ]
        .filter(Boolean)
        .join(" | ");

      doc.text(contactLine, leftMargin, y);
      y += lineHeight;

      // Address: Handling long text with splitTextToSize
      const addressParts = [
        buyer?.addressline1,
        [buyer?.state, buyer?.district, buyer?.country]
          .filter(Boolean)
          .join(", "),
        buyer?.pincode ? `- ${buyer?.pincode}` : null,
      ]
        .filter(Boolean)
        .join(", ");

      if (addressParts) {
        const splitAddress = doc.splitTextToSize(
          addressParts,
          pageWidth - mm(20),
        );
        splitAddress.forEach((line) => {
          doc.text(line, leftMargin, y);
          y += lineHeight;
        });
      }

      // 4. Statutory IDs (GST, PAN, CIN): Compact layout
       const idList = [
      { label: "GST: ", value: buyer.gstnumber, bold: true,},      
      { label: "PAN: ", value: buyer.pannumber ,bold: true,},
      { label: "CIN: ", value: buyer.cinnumber,bold: true, },
      { label: "FSSAI: ", value: buyer.fssai,bold: true, },
      { label: "Reference No: ", value: buyer.referenceid, bold: false, },
    ];

      idList.forEach((item) => {
        if (item?.value) {
          const leftMargin = mm(8);
if (item.bold) {
      doc.setFont("NotoSansMono-Bold", "bold");
      } else {
        doc.setFont("NotoSansMono-Regular", "normal");
      }  
          // 1. Draw the Label in BOLD
         doc.setFontSize(6);
          doc.text(item?.label, leftMargin, y);

          // 2. Calculate label width so the value starts exactly after it
          const labelWidth = doc.getTextWidth(item?.label);

          // 3. Draw the Value in REGULAR
          doc.setFont("NotoSansMono-Regular", "normal");
          doc.text(`${item?.value}`, leftMargin + labelWidth, y);

          // 4. Move to next line
          y += mm(7);
        }
      });
    }
  }
  const tableMargin = mm(6);
  const tableWidth = pageWidth - tableMargin * 2;

  autoTable(doc, {
    startY: y,
    margin: { left: tableMargin, right: tableMargin },
    theme: "plain",
    // 1. Set global border styles
    styles: {
      fontSize: 6,
      cellPadding: { top: 0.5, right: 1, bottom: 0.2, left: 1 },
      lineColor: "black", // Light gray border
      lineWidth: 1,
      font: "NotoSansMono-Regular",
    },
    head: [
      // Header Row 1: "Items" label span
      [
        {
          content: "Items",
          cellPadding: 1,
          colSpan: 3,
          styles: {
            halign: "left",
            fontStyle: "bold",
            lineWidth: { left: mm(1), right: mm(1), top: mm(1), bottom: 0 },
          },
        },
      ],
      // Header Row 2: Rate, Qty, Total
      [
        {
          content: "Rate",
          cellPadding: 1,
          styles: {
            halign: "center",
            fontStyle: "bold",
            lineWidth: { left: mm(1), top: 0, bottom: mm(1) },
          },
        },
        {
          content: "Qty",
          cellPadding: 1,
          styles: {
            halign: "center",
            fontStyle: "bold",
            lineWidth: { top: 0, bottom: mm(0.8) },
          },
        },
        {
          content: "Total",
          cellPadding: 1,
          styles: {
            halign: "center",
            fontStyle: "bold",
            lineWidth: { right: mm(1), top: 0, bottom: mm(1) },
          },
        },
      ],
    ],
    body: documentConfig?.items.flatMap((item) => [
      // Line 1: Item Name (Full width)
      [
        {
          content: item?.title + (item?.note ? ` (${item?.note})` : ""),
          colSpan: 3,
          styles: {
            halign: "left",
            lineWidth: { left: mm(1), right: mm(1), top: 0, bottom: 0 },
          },
        },
      ],
      // Line 2: Values (Rate, Qty, Total)
      [
        {
          content: `${item?.soldprice}`,
          styles: {
            halign: "center",
            lineWidth: { left: mm(1), top: 0, bottom: 0 },
          },
        },
        {
          content: `${item?.quantity} ${(() => {
            const unitCode = item?.isSecondaryUnit
              ? item?.unitConfig?.secondaryUnitCode
              : item?.unitConfig?.primaryUnitCode;
            return unitCode || "";
          })()}`,
          styles: { halign: "center", lineWidth: { top: 0, bottom: 0 } },
        },
        {
          content: `${item?.lineTotalSoldPrice}`,
          styles: {
            halign: "center",
            lineWidth: { right: mm(1), top: 0, bottom: 0 },
          },
        },
      ],
    ]),
    // 3. Close the bottom border on the last row
    didDrawCell: (data) => {
      if (data.row.index === data.table.body.length - 1) {
        doc.setDrawColor(0);
        doc.setLineWidth(mm(1));
        doc.line(
          data.cell.x,
          data.cell.y + data.cell.height,
          data.cell.x + data.cell.width,
          data.cell.y + data.cell.height,
        );
      }
    },
  });

  y = doc.lastAutoTable.finalY + mm(5);

  // Set dimensions for the boxes
  const boxPadding = 8;
  let boxWidth = (pageWidth - mm(24)) / 2; // Split page width minus margins
  const boxHeight = mm(30); // Height to accommodate two lines of text
  const startX = mm(8);
  const rightBoxX = startX + boxWidth + mm(8);

  // --- 1. Draw Left Box (Qty & Items) ---
  doc.setDrawColor(0); // Changed from 200 to 150 for a darker, printable gray
  doc.setLineWidth(mm(1)); // Light gray border
  doc.roundedRect(startX, y, boxWidth, boxHeight, mm(3), mm(3), "S");

  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(7);
  // Qty Line
  // doc.text("Qty :", startX + mm(5), y + mm(10));
  // doc.text(`${data.totalqty || 0}`, startX + boxWidth - mm(5), y + mm(10), { align: "right" });
  // Items Line
  doc.text("Items :", startX + mm(5), y + mm(18));
  doc.text(`${data?.totalItems}`, startX + boxWidth - mm(5), y + mm(18), {
    align: "right",
  });

  // --- 2. Draw Right Box (Total Amount) ---
  doc.roundedRect(rightBoxX, y, boxWidth, boxHeight, mm(3), mm(3), "S");

  doc.setFont("NotoSansMono-Bold", "bold").setFontSize(7);
  const totalLabel = "Total Amount";
  const totalValue = `${documentConfig?.totalBasePrice}`; // Assuming raw number as per UI
  const boxCenterX = rightBoxX + boxWidth / 2;

  // Draw Label: Centered horizontally
  doc.text(totalLabel, boxCenterX, y + mm(10), { align: "center" });

  doc.setFontSize(7);
  doc.text(totalValue, boxCenterX, y + mm(22), { align: "center" });

  // // Center the content within the right box
  // const combinedText = `${totalLabel} ${totalValue}`;
  // const textWidth1 = doc.getTextWidth(combinedText);
  // const textStartX = rightBoxX + (boxWidth - textWidth1) / 2;

  // doc.text(totalLabel, textStartX, y + 26);
  // doc.text(totalValue, textStartX + doc.getTextWidth(totalLabel) + 4, y + 26);

  y += boxHeight + mm(7); // Advance Y position for next section

  // 1. Prepare payment list (Only show modes with value)
  const payments = [
    { label: "Cash", amount: documentConfig?.cashPaid },
    { label: "Card", amount: documentConfig?.cardPaid },
    {
      label: "Online Pay",
      amount: documentConfig?.onlinePaid,
    },
    { label: "UPI", amount: documentConfig?.upiPaid },
    { label: "Balance Due", amount: documentConfig?.balanceDue },
  ].filter((p) => Math.abs(parseAmount(p.amount)) > 0);
  if (payments.length > 0) {
    // y = checkPageBreak(doc, y);
    const boxMargin = mm(8);
    autoTable(doc, {
      startY: y,
      theme: "plain",
      margin: { left: boxMargin, right: boxMargin },
      styles: {
        font: "NotoSansMono-Regular",
        fontSize: 5,
        cellPadding: { top: 0.5, right: 1, bottom: 0.2, left: 1 },
        lineColor: "black",
        lineWidth: 1,
      },
      head:hasPayment
    ?  [
        [
          {
            content: "PAYMENT MODE",
            colSpan: 3,
            cellPadding: 1,
            styles: {
              font: "NotoSansMono-Regular",
              fontSize: 5,
              fontStyle: "normal",
              lineWidth: {
                top: mm(1),
                left: mm(1),
                right: mm(1),
                bottom: mm(1),
              },
            },
          },
        ],
      ]:[],
      body: [
        ...(hasPayment
      ?payments?.map((p) => [
          {
            content: p.label,
            styles: { fontSize: 5, lineWidth: { left: mm(1) } },
          },
          { content: ":", styles: { halign: "center", lineWidth: 0 } },
          {
            content: `${(p?.amount)}`,
            styles: {
              halign: "right",
              lineWidth: { right: mm(1) },
              fontSize: 5,
            },
          },
        ]):[]),
        // Bottom Right Footer for Tender and Balance
        [
          {
            // content: `Tender: ${CurrencyConverter.toCurrency(data.cashamount || 0||0, "INR")}\nBalance: ${CurrencyConverter.toCurrency(data.cashchange || 0||0, "INR")}`,
            content: `Grand Total:   ${documentConfig?.grandTotal}`,
            colSpan: 5,
            styles: {
              halign: "right",
              font: "NotoSansMono-Bold",
              fontStyle: "bold",
              fontSize: 8,
              cellPadding: 2,
              lineWidth: {
                left: mm(1),
                right: mm(1),
                bottom: mm(1),
                top: mm(1),
              },
            },
          },
        ],
      ],
      columnStyles: {
        0: { cellWidth: "auto" },
        1: { cellWidth: "20" },
        2: { cellWidth: "auto" },
      },
    });

    y = doc.lastAutoTable.finalY + mm(7);
  }

  // ---------- TAX TABLE SECTION (FULL) ----------

  // 1. Build Tax Head
  const taxHead = isIGST
    ? [["Tax", "Amount", "IGST"]]
    : [["Tax", "Amount", "SGST", "CGST"]];

  // 2. Build Tax Body
  const taxBody = Object.entries(taxRows)
    .filter(([percent]) => parseFloat(percent) > 0)
    .map(([percent, t]) =>
      isIGST
        ? [`${percent}%`, `${t?.basePrice}`, `${t?.igst}`]
        : [`${percent}%`, `${t?.basePrice}`, `${t?.sgst}`, `${t?.cgst}`],
    );

  // 3. Render Table Only If Tax Exists
  if (taxBody?.length > 0) {
    const tableMargin = mm(8);

    autoTable(doc, {
      startY: y,
      margin: { left: tableMargin, right: tableMargin },
      theme: "plain",

      styles: {
        fontSize: 5,
        cellPadding: { top: 0.5, right: 1, bottom: 0.2, left: 1 },
        lineColor: "black",
        lineWidth: 0,
        font: "NotoSansMono-Regular",
        fontStyle: "normal",
      },

      // 4. Dynamic Head
      head: taxHead.map((row) =>
        row.map((cell, colIndex) => ({
          content: cell,
          styles: {
            halign: colIndex === 0 ? "left" : "right",
            font: "NotoSansMono-Regular",
            fontStyle: "normal",
            lineWidth: {
              top: mm(1),
              bottom: 0,
              left: colIndex === 0 ? mm(1) : 0,
              right: colIndex === row.length - 1 ? mm(1) : 0,
            },
          },
        })),
      ),

      // 5. Dynamic Body
      body: taxBody?.map((row) =>
        row.map((cell, colIndex) => ({
          content: cell,
          styles: {
            halign: colIndex === 0 ? "left" : "right",
            lineWidth: {
              left: colIndex === 0 ? mm(1) : 0,
              right: colIndex === row.length - 1 ? mm(1) : 0,
              top: 0,
              bottom: 0,
            },
          },
        })),
      ),

      // 6. Draw Bottom Border for Last Row
      didDrawCell: (data) => {
        if (
          data.row.section === "body" &&
          data.row.index === data.table.body.length - 1
        ) {
          doc.setDrawColor(0);
          doc.setLineWidth(mm(1));
          doc.line(
            data.cell.x,
            data.cell.y + data.cell.height,
            data.cell.x + data.cell.width,
            data.cell.y + data.cell.height,
          );
        }
      },
    });

    // 7. Move Y After Tax Table
    y = doc.lastAutoTable.finalY + mm(5);
  }
  //  y = checkPageBreak(doc, y);
  if (parseAmount(documentConfig?.totalDiscount) > 0) {
    const boxMargin = mm(8);
    const boxHeight = mm(15);
    const boxWidth = pageWidth - boxMargin * 2;
    // o 1
    // 1. Draw the Container Box
    doc.setDrawColor(0); // Light Gray border
    doc.setLineWidth(mm(1));
    doc.rect(boxMargin, y, boxWidth, boxHeight, "S");

    // 2. Set Font for the content
    doc.setFont("NotoSansMono-Regular", "normal").setFontSize(5);

    const savedAmount = `${documentConfig?.totalDiscount}`;
    doc.text(`YOU SAVED :  ${savedAmount}`, boxMargin + mm(4), y + mm(9));

    // doc.text(savedAmount, pageWidth - (boxMargin + 4), y + 14, { align: "right" });

    y += boxHeight + mm(8);
  }
  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(12);

  // y += mm(5);
  doc.setFontSize(7);
  doc.text("Thank you for your purchase!", pageWidth / 2, y, {
    align: "center",
  });
  // y += mm(10);
  // doc.setFont("NotoSansMono-Bold", "bold");
  // doc.text("** Visit us Again **", pageWidth / 2, y, { align: "center" });
  const thankBoxPadding = 10;
  y += mm(8);

  doc.setFont("NotoSansMono-Regular", "normal");
  doc.text("Powered By", pageWidth / 2, y, { align: "center" });
  y += mm(8);
  doc.setFontSize(8);
  const sitePrefix = "www.";
  const siteBold = "NegoBill";
  const siteSuffix = ".com";
  const prefixWidth = doc.getTextWidth(sitePrefix);
  const boldWidth = doc.getTextWidth(siteBold);
  const suffixWidth = doc.getTextWidth(siteSuffix);
  const totalWidth = prefixWidth + boldWidth + suffixWidth;
  const startX1 = (pageWidth - totalWidth) / 2;
  if (storeSettings?.negobillBranding) {
    doc.setFont("NotoSansMono-Regular", "normal");
    doc.text(sitePrefix, startX1, y);
    doc.setFont("NotoSansMono-Bold", "bold");
    doc.text(siteBold, startX1 + prefixWidth, y);
    doc.setFont("NotoSansMono-Regular", "normal");
    doc.text(siteSuffix, startX1 + prefixWidth + boldWidth, y);
    y += mm(3);
  }
  // setPrint(false);
  return doc;
};

export const Generate2InchBillReceiptM2 = async (
  data,
  qrCodeDataUrl,
  terms,
  invoiceConfig,
  selectedTheme,  
  storeSettings
) => {
   const {
    documentConfig,hasPayment       
  } = invoiceConfig;
 

  const mm = (pt) => pt / 2.83465;

  const itemHeight = documentConfig?.items.length * mm(45);
  const taxHeight = Object.keys(documentConfig?.items).length * mm(25);
  const footerHeight = mm(250);
  const headerHeight = mm(250);
  const dynamicHeight = headerHeight + itemHeight + taxHeight + footerHeight;

  const doc = new jsPDF({
    orientation: "p",
    unit: "mm",
    format: [49, dynamicHeight + mm(30)],
  });

  const pageWidth = doc.internal.pageSize.getWidth();

  let y = mm(10);

  const isIGST =
    documentConfig?.fromData?.statecode &&
    documentConfig?.toData?.statecode &&
    documentConfig?.fromData.statecode !== documentConfig?.toData.statecode;

  const taxRows = documentConfig?.items?.reduce((acc, product) => {
    const taxPercentage = product?.taxpercentage || 0;
    const taxAmount = product?.lineTotalTaxAmount || 0;
    const basePrice = product?.lineTotalBasePrice || 0;
    if (!acc[taxPercentage]) {
      acc[taxPercentage] = { basePrice: 0, sgst: 0, cgst: 0, igst: 0 };
    }
    acc[taxPercentage].basePrice += basePrice;
    if (isIGST) {
      acc[taxPercentage].igst += taxAmount;
    } else {
      acc[taxPercentage].sgst += taxAmount / 2;
      acc[taxPercentage].cgst += taxAmount / 2;
    }
    return acc;
  }, {});
  const checkPageBreak = (doc, y, buffer = mm(20)) => {
    const pageHeight = doc.internal.pageSize.getHeight();
    if (y + buffer >= pageHeight) {
      doc.addPage();
      return mm(14);
    }
    return y;
  };
  // const storeBoxStartY = y;


     const imagePath =  documentConfig?.image;

  if (documentConfig?.image) {
    try {
      // Step 1: Convert image URL to base64
      const response = await fetch(imagePath, { mode: "cors" });
      const blob = await response.blob();
      let base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

      // Step 2: Convert to grayscale
      base64 = await convertToGrayScale(base64);

      // Step 3: Get natural dimensions
      const { width: naturalWidth, height: naturalHeight } =
        await loadImageDimensions(base64);

      // Step 4: Resize to fit dynamically within maxWidth and maxHeight
      const maxWidth = mm(50); // maximum width in pt for 3-inch receipt
      const maxHeight = mm(40); // maximum height in pt
      const widthRatio = maxWidth / naturalWidth;
      const heightRatio = maxHeight / naturalHeight;
      const ratio = Math.min(widthRatio, heightRatio, 1); // don't upscale

      const imgWidth = naturalWidth * ratio;
      const imgHeight = naturalHeight * ratio;

      // Step 5: Center the logo
      const logoX = (pageWidth - imgWidth) / 2;

      doc.addImage(base64, "PNG", logoX, y, imgWidth, imgHeight);

      y += imgHeight + mm(10); // spacing after logo
    } catch (err) {
      console.warn("Logo failed to load:", err);
    }
  }

  let margin = mm(6);

  // Header

  doc.setFont("NotoSansMono-Bold", "bold");

  doc.setFontSize(6);
  const storeTitle =   documentConfig?.fromData?.title || "Store";
  doc.splitTextToSize(storeTitle, pageWidth - mm(30)).forEach((line) => {
    doc.text(line, pageWidth / 2, y, { align: "center" });
    y += mm(7);
  });
  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(6);
  const seller = documentConfig?.fromData;
  const sellerAddressParts = [
    seller?.addressline1,
    [seller?.state].filter(Boolean).join(", "),
  ].filter(Boolean);
  doc
    .splitTextToSize(sellerAddressParts.join(", "), pageWidth - mm(50))
    .forEach((line) => {
      doc.text(line, pageWidth / 2, y, { align: "center" });
      y += mm(7);
    });

  // if (seller?.email) {
  //   doc.text(`Email: ${seller.email}`, pageWidth / 2, y, { align: "center" });
  //   y += 16;
  // }
  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(6); // Slightly smaller to match UI

  const addressText = `${seller?.district} ${seller?.country} - ${seller?.pincode}`;
  doc.text(addressText, pageWidth / 2, y, { align: "center" });
  y += mm(7);
  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(6);
  const cinnumber = `CIN: ${seller?.cinnumber || ""}`;
  const phoneNumbers = [
    documentConfig?.fromData?.phone,
    documentConfig?.fromData?.additionalPhone,
  ].filter(Boolean);

  const phoneText = phoneNumbers.length
    ? `Ph: ${phoneNumbers.join(", ")}`
    : "";
  const panTex = `PAN: ${seller?.pannumber || ""}`;

  const horizontalPadding = mm(6);
  const leftStart = horizontalPadding;
  const rightEnd = pageWidth - horizontalPadding;
 if (phoneText) {
  const phoneMaxWidth = pageWidth - mm(20);

  const phoneLines = doc.splitTextToSize(
    phoneText,
    phoneMaxWidth,
  );

  doc.text(phoneLines, pageWidth / 2, y, {
    align: "center",
  });

  // Move according to actual phone lines
  y += phoneLines.length * mm(4) +1;
}
  doc.text(panTex, leftStart, y, { align: "left" });
  // doc.text(phoneText, rightEnd, y, { align: "right" });
  y += mm(7);

  const fassiTest = `FSSAI: ${seller?.fssai || ""}`;

  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(6);
  if (seller?.fssai) {
    doc.text(fassiTest, leftStart, y, { align: "left" });
    y += mm(7);
  }
  {
    seller?.cinnumber && doc.text(cinnumber, leftStart, y, { align: "left" });
  }
  y += mm(7);

  // y += 7;
  // if (seller?.cinnumber) {
  //   const cinTest = `CIN: ${seller?.cinnumber || ""}`;

  //   doc.setFont("NotoSansMono-Regular", "normal").setFontSize(6);
  //   doc.text(cinTest, leftStart, y, { align: "left" });
  //   y += 7;
  // }
  const storeBoxPadding = 4;

  doc.setLineWidth(mm(1));
  doc.setLineDash([mm(1), mm(1)]); // solid border

  const invoiceTitle =
    data?.status === "CANCELLED"
      ? "CANCELLED TAX INVOICE"
      :documentConfig?.title;

  doc.setFont("NotoSansMono-Bold", "bold").setFontSize(7);
  const textWidth = doc.getTextWidth(invoiceTitle);
  const gap = mm(8); // Space between the line and the text
  margin = mm(10);
  // 5. Draw the Lines
  let centerX = pageWidth / 2;
  doc.text(invoiceTitle, centerX, y, { align: "center" });
  doc.setLineWidth(mm(1));
  doc.setLineDash([mm(3), mm(3)]); // Use [0] for a solid line, [2, 2] for dashed like your image

  // Left Line: starts at margin, ends just before the text
  doc.line(margin, y - mm(4), centerX - textWidth / 2 - gap, y - mm(4));

  // Right Line: starts just after the text, ends at margin
  doc.line(
    centerX + textWidth / 2 + gap,
    y - mm(4),
    pageWidth - margin,
    y - mm(4),
  );
  doc.setLineDash([]);
  y += mm(7);

  const { date, time } = documentConfig?.Date;
  const lineHeight = mm(7); // Adjusted for tighter spacing seen in UI
  const leftColX = mm(8); // Left margin
  const rightColX = pageWidth / 2 + mm(10); // Start of the second column
  const drawField = (label, value, xPos, yPos) => {
    doc.setFont("NotoSansMono-Regular", "normal").setFontSize(7);
    const fieldText = `${label} : ${value}`;
    doc.text(fieldText, xPos, yPos);
  };
  drawField( documentConfig?.numberlabel, `${ documentConfig?.number}`, leftColX, y);
  // drawField("Counter", "02", rightColX, y); // Replace "02" with your dynamic counter data
  y += lineHeight;

  // Row 2: Date
  drawField("Date", `${date}, ${time}`, leftColX, y);
  y += lineHeight;

  // Row 3: Cashier
  // drawField("Cashier", "Stejo", leftColX, y); // Replace "Stejo" with your dynamic cashier data
  y += mm(5);
  // buyer info

  const buyer = documentConfig?.toData;
  const hasBuyerInfo =
    buyer &&
    (buyer?.name ||
      buyer?.phone ||
      buyer?.addressline1 ||
      buyer?.email ||
      buyer?.gstnumber ||
      buyer?.pannumber ||
      buyer?.cinnumber);
  if (hasBuyerInfo) {
    doc.setFont("NotoSansMono-Bold", "bold").setFontSize(7);
    doc.text("BUYER INFORMATION", pageWidth / 2, y, { align: "center" });
    y += mm(7);
    const leftMargin = mm(10);
    const lineHeight = mm(7);
    if (buyer?.name) {
      doc.setFont("NotoSansMono-Bold", "bold").setFontSize(6);
      doc.text(buyer?.name.toUpperCase(), leftMargin, y);
      y += lineHeight;
    }
    doc.setFont("NotoSansMono-Regular", "normal").setFontSize(6);
    if (buyer?.phone || buyer?.email) {
      const contactLine = [
        buyer?.phone ? `Ph: ${buyer?.phone}` : null,
        buyer?.email ? `Email: ${buyer?.email}` : null,
      ]
        .filter(Boolean)
        .join(" | ");

      doc.text(contactLine, leftMargin, y);
      y += lineHeight;

      // Address: Handling long text with splitTextToSize
      const addressParts = [
        buyer?.addressline1,
        [buyer?.state, buyer?.district, buyer?.country]
          .filter(Boolean)
          .join(", "),
        buyer?.pincode ? `- ${buyer?.pincode}` : null,
      ]
        .filter(Boolean)
        .join(", ");

      if (addressParts) {
        const splitAddress = doc.splitTextToSize(
          addressParts,
          pageWidth - mm(20),
        );
        splitAddress.forEach((line) => {
          doc.text(line, leftMargin, y);
          y += lineHeight;
        });
      }

      // 4. Statutory IDs (GST, PAN, CIN): Compact layout
       const idList = [
      { label: "GST: ", value: buyer.gstnumber, bold: true,},      
      { label: "PAN: ", value: buyer.pannumber ,bold: true,},
      { label: "CIN: ", value: buyer.cinnumber,bold: true, },
      { label: "FSSAI: ", value: buyer.fssai,bold: true, },
      { label: "Reference No: ", value: buyer.referenceid, bold: false, },
    ];

      idList.forEach((item) => {
        if (item?.value) {
          const leftMargin = mm(10);
if (item.bold) {
      doc.setFont("NotoSansMono-Bold", "bold");
      } else {
        doc.setFont("NotoSansMono-Regular", "normal");
      }  
          // 1. Draw the Label in BOLD
         doc.setFontSize(6);
          doc.text(item?.label, leftMargin, y);

          // 2. Calculate label width so the value starts exactly after it
          const labelWidth = doc.getTextWidth(item?.label);

          // 3. Draw the Value in REGULAR
          doc.setFont("NotoSansMono-Regular", "normal");
          doc.text(`${item?.value}`, leftMargin + labelWidth, y);

          // 4. Move to next line
          y += mm(7);
        }
      });
    }
  }
  const tableMargin = mm(6);
  const tableWidth = pageWidth - tableMargin * 2;

  autoTable(doc, {
    startY: y,
    margin: { left: tableMargin, right: tableMargin },
    theme: "plain",
    // 1. Set global border styles
    styles: {
      fontSize: 7,
      cellPadding: { top: 0.5, right: 1, bottom: 0.2, left: 1 },
      lineColor: "black", // Light gray border
      lineWidth: 1,
      font: "NotoSansMono-Regular",
    },
    head: [
      // Header Row 1: "Items" label span
      [
        {
          content: "Items",
          cellPadding: 1,
          colSpan: 3,
          styles: {
            halign: "left",
            fontStyle: "bold",
            lineWidth: { left: mm(1), right: mm(1), top: mm(1), bottom: 0 },
          },
        },
      ],
      // Header Row 2: Rate, Qty, Total
      [
        {
          content: "Rate",
          cellPadding: 1,
          styles: {
            halign: "center",
            fontStyle: "bold",
            lineWidth: { left: mm(1), top: 0, bottom: mm(1) },
          },
        },
        {
          content: "Qty",
          cellPadding: 1,
          styles: {
            halign: "center",
            fontStyle: "bold",
            lineWidth: { top: 0, bottom: mm(1) },
          },
        },
        {
          content: "Total",
          cellPadding: 1,
          styles: {
            halign: "center",
            fontStyle: "bold",
            lineWidth: { right: mm(1), top: 0, bottom: mm(1) },
          },
        },
      ],
    ],
    body: documentConfig?.items.flatMap((item) => [
      // Line 1: Item Name (Full width)
      [
        {
          content: item?.title + (item?.note ? ` (${item?.note})` : ""),
          colSpan: 3,
          styles: {
            halign: "left",
            lineWidth: { left: mm(1), right: mm(1), top: 0, bottom: 0 },
          },
        },
      ],
      // Line 2: Values (Rate, Qty, Total)
      [
        {
          content: `${item?.soldprice}`,
          styles: {
            halign: "center",
            lineWidth: { left: mm(1), top: 0, bottom: 0 },
          },
        },
        {
          content: `${item?.quantity} ${(() => {
            const unitCode = item?.isSecondaryUnit
              ? item?.unitConfig?.secondaryUnitCode
              : item?.unitConfig?.primaryUnitCode;
            return unitCode || "";
          })()}`,
          styles: { halign: "center", lineWidth: { top: 0, bottom: 0 } },
        },
        {
          content: `${item?.lineTotalSoldPrice}`,
          styles: {
            halign: "center",
            lineWidth: { right: mm(1), top: 0, bottom: 0 },
          },
        },
      ],
    ]),
    // 3. Close the bottom border on the last row
    didDrawCell: (data) => {
      if (data.row.index === data.table.body.length - 1) {
        doc.setDrawColor(0);
        doc.setLineWidth(mm(1));
        doc.line(
          data.cell.x,
          data.cell.y + data.cell.height,
          data.cell.x + data.cell.width,
          data.cell.y + data.cell.height,
        );
      }
    },
  });

  y = doc.lastAutoTable.finalY + mm(8);

  // Set dimensions for the boxes
  const boxPadding = 8;
  let boxWidth = (pageWidth - mm(24)) / 2; // Split page width minus margins
  const boxHeight = mm(30); // Height to accommodate two lines of text
  const startX = mm(8);
  const rightBoxX = startX + boxWidth + mm(8);

  // --- 1. Draw Left Box (Qty & Items) ---
  doc.setDrawColor(0); // Changed from 200 to 150 for a darker, printable gray
  doc.setLineWidth(mm(1)); // Light gray border
  doc.roundedRect(startX, y, boxWidth, boxHeight, mm(3), mm(3), "S");

  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(7);
  // Qty Line
  // doc.text("Qty :", startX + mm(5), y + mm(10));
  // doc.text(`${data.totalqty || 0}`, startX + boxWidth - mm(5), y + mm(10), { align: "right" });
  // Items Line
  doc.text("Items :", startX + mm(5), y + mm(18));
  doc.text(`${data?.totalItems}`, startX + boxWidth - mm(5), y + mm(18), {
    align: "right",
  });

  // --- 2. Draw Right Box (Total Amount) ---
  doc.roundedRect(rightBoxX, y, boxWidth, boxHeight, mm(3), mm(3), "S");

  doc.setFont("NotoSansMono-Bold", "bold").setFontSize(7);
  const totalLabel = "Grand Total";
  const totalValue = `${documentConfig?.grandTotal}`; // Assuming raw number as per UI
  const boxCenterX = rightBoxX + boxWidth / 2;

  // Draw Label: Centered horizontally
  doc.text(totalLabel, boxCenterX, y + mm(10), { align: "center" });

  doc.setFontSize(7);
  doc.text(totalValue, boxCenterX, y + mm(22), { align: "center" });

  // // Center the content within the right box
  // const combinedText = `${totalLabel} ${totalValue}`;
  // const textWidth1 = doc.getTextWidth(combinedText);
  // const textStartX = rightBoxX + (boxWidth - textWidth1) / 2;

  // doc.text(totalLabel, textStartX, y + 26);
  // doc.text(totalValue, textStartX + doc.getTextWidth(totalLabel) + 4, y + 26);

  y += boxHeight + mm(7); // Advance Y position for next section

  // 1. Prepare payment list (Only show modes with value)
  const payments = [
    { label: "Cash", amount: documentConfig?.cashPaid },
    { label: "Card", amount: documentConfig?.cardPaid },
    {
      label: "Online Pay",
      amount: documentConfig?.onlinePaid,
    },
    { label: "UPI", amount: documentConfig?.upiPaid },
    { label: "Balance Due", amount: documentConfig?.balanceDue },
  ].filter((p) => Math.abs(parseAmount(p.amount)) > 0);
  if (payments.length > 0) {
    // y = checkPageBreak(doc, y);
    const boxMargin = mm(8);
    autoTable(doc, {
      startY: y,
      theme: "plain",
      margin: { left: boxMargin, right: boxMargin },
      styles: {
        font: "NotoSansMono-Regular",
        fontSize: 6,
        cellPadding: { top: 0.5, right: 1, bottom: 0.2, left: 1 },
        lineColor: "black",
        lineWidth: 1,
      },
      head: hasPayment
    ? [
        [
          {
            content: "PAYMENT MODE",
            colSpan: 3,
            styles: {
              font: "NotoSansMono-Bold",
              fontSize: 6,
              cellPadding: 1,
              fontStyle: "bold",
              lineWidth: {
                top: mm(1),
                left: mm(1),
                right: mm(1),
                bottom: mm(1),
              },
            },
          },
        ],
      ]:[],
      body: [
       ...(hasPayment
      ?payments.map((p) => [
          {
            content: p?.label,
            styles: { fontSize: 6, lineWidth: { left: mm(0.5) } },
          },
          { content: ":", styles: { halign: "center", lineWidth: 0 } },
          {
            content: `${(p?.amount)}`,
            styles: {
              halign: "right",
              lineWidth: { right: mm(1) },
              fontSize: 6,
            },
          },
        ]):[]),
        // Bottom Right Footer for Tender and Balance
        [
          {
            // content: `Tender: ${CurrencyConverter.toCurrency(data.cashamount || 0||0, "INR")}\nBalance: ${CurrencyConverter.toCurrency(data.cashchange || 0||0, "INR")}`,
            content: `Grand Total:   ${documentConfig?.grandTotal}`,
            colSpan: 5,
            styles: {
              halign: "right",
              font: "NotoSansMono-Bold",
              fontStyle: "bold",
              fontSize: 8,
              cellPadding: 2,
              lineWidth: {
                left: mm(1),
                right: mm(1),
                bottom: mm(1),
                top: mm(1),
              },
            },
          },
        ],
      ],
      columnStyles: {
        0: { cellWidth: "auto" },
        1: { cellWidth: "20" },
        2: { cellWidth: "auto" },
      },
    });

    y = doc.lastAutoTable.finalY + mm(7);
  }

  // ---------- TAX TABLE SECTION (FULL) ----------

  // 1. Build Tax Head
  // const taxHead = isIGST
  // ? [["Tax", "Amount", "IGST"]]
  // : [["Tax", "Amount", "SGST", "CGST"]];

  // // 2. Build Tax Body
  // const taxBody = Object.entries(taxRows)
  // .filter(([percent]) => parseFloat(percent) > 0)
  // .map(([percent, t]) =>
  //   isIGST
  //     ? [
  //         `${percent}%`,
  //         `${CurrencyConverter.toCurrency(t.basePrice || 0, "INR")}`,
  //         `${CurrencyConverter.toCurrency(t.igst || 0, "INR")}`,
  //       ]
  //     : [
  //         `${percent}%`,
  //         `${CurrencyConverter.toCurrency(t.basePrice || 0, "INR")}`,
  //         `${CurrencyConverter.toCurrency(t.sgst || 0, "INR")}`,
  //         `${CurrencyConverter.toCurrency(t.cgst || 0, "INR")}`,
  //       ]
  // );

  // // 3. Render Table Only If Tax Exists
  // if (taxBody.length > 0) {
  // const tableMargin = 8;

  // autoTable(doc, {
  //   startY: y,
  //   margin: { left: tableMargin, right: tableMargin },
  //   theme: "plain",

  //   styles: {
  //     fontSize: 7,
  //     cellPadding: 4,
  //     lineColor: "black",
  //     lineWidth: 0,
  //     font: "NotoSansMono-Regular",
  //     fontStyle: "normal",
  //   },

  //   // 4. Dynamic Head
  //   head: taxHead.map((row) =>
  //     row.map((cell, colIndex) => ({
  //       content: cell,
  //       styles: {
  //         halign: colIndex === 0 ? "left" : "right",
  //         font: "NotoSansMono-Bold" ,
  //         fontStyle: "bold",
  //         lineWidth: {
  //           top: 1,
  //           bottom: 0,
  //           left: colIndex === 0 ? 1 : 0,
  //           right: colIndex === row.length - 1 ? 1 : 0,
  //         },
  //       },
  //     }))
  //   ),

  //   // 5. Dynamic Body
  //   body: taxBody.map((row) =>
  //     row.map((cell, colIndex) => ({
  //       content: cell,
  //       styles: {
  //         halign: colIndex === 0 ? "left" : "right",
  //         lineWidth: {
  //           left: colIndex === 0 ? 1 : 0,
  //           right: colIndex === row.length - 1 ? 1 : 0,
  //           top: 0,
  //           bottom: 0,
  //         },
  //       },
  //     }))
  //   ),

  //   // 6. Draw Bottom Border for Last Row
  //   didDrawCell: (data) => {
  //     if (
  //       data.row.section === "body" &&
  //       data.row.index === data.table.body.length - 1
  //     ) {
  //       doc.setDrawColor(0);
  //       doc.setLineWidth(1);
  //       doc.line(
  //         data.cell.x,
  //         data.cell.y + data.cell.height,
  //         data.cell.x + data.cell.width,
  //         data.cell.y + data.cell.height
  //       );
  //     }
  //   },
  // });

  // // 7. Move Y After Tax Table
  // y = doc.lastAutoTable.finalY + 5;
  // }
  //  y = checkPageBreak(doc, y);
  if (parseAmount(documentConfig?.totalDiscount) > 0) {
    const boxMargin = mm(8);
    const boxHeight = mm(18);
    const boxWidth = pageWidth - boxMargin * 2;
    // o 1
    // 1. Draw the Container Box
    doc.setDrawColor(0); // Light Gray border
    doc.setLineWidth(mm(1));
    doc.rect(boxMargin, y, boxWidth, boxHeight, "S");

    // 2. Set Font for the content
    doc.setFont("NotoSansMono-Regular", "normal").setFontSize(7);

    const savedAmount = `${documentConfig?.totalDiscount}`;
    doc.text(`YOU SAVED :  ${savedAmount}`, boxMargin + mm(4), y + mm(12));

    // doc.text(savedAmount, pageWidth - (boxMargin + 4), y + 14, { align: "right" });

    y += boxHeight + mm(7);
  }
  doc.setFont("NotoSansMono-Regular", "normal").setFontSize(12);

  y += mm(6);
  doc.setFontSize(7);
  doc.text("Thank you for your purchase!", pageWidth / 2, y, {
    align: "center",
  });
  y += mm(9);
  //   doc.setFont("NotoSansMono-Bold", "bold");
  //   doc.text("** Visit us Again **", pageWidth / 2, y, { align: "center" });
  //   const thankBoxPadding=10;
  // y += mm(16);

  doc.setFont("NotoSansMono-Regular", "normal");
  doc.text("Powered By", pageWidth / 2, y, { align: "center" });
  y += mm(9);
  doc.setFontSize(7);
  const sitePrefix = "www.";
  const siteBold = "NegoBill";
  const siteSuffix = ".com";
  const prefixWidth = doc.getTextWidth(sitePrefix);
  const boldWidth = doc.getTextWidth(siteBold);
  const suffixWidth = doc.getTextWidth(siteSuffix);
  const totalWidth = prefixWidth + boldWidth + suffixWidth;
  const startX1 = (pageWidth - totalWidth) / 2;
  if (storeSettings?.negobillBranding) {
    doc.setFont("NotoSansMono-Regular", "normal");
    doc.text(sitePrefix, startX1, y);
    doc.setFont("NotoSansMono-Bold", "bold");
    doc.text(siteBold, startX1 + prefixWidth, y);
    doc.setFont("NotoSansMono-Regular", "normal");
    doc.text(siteSuffix, startX1 + prefixWidth + boldWidth, y);
    y += mm(3);
  }
  // setPrint(false);
  return doc;
};
